// src/components/settings/Settings.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Settings (Liquid Glassmorphism)
//  "TelcoFair Glass Workspace" — full glassmorphism with ALL settings tabs
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  useMediaQuery,
  useTheme,
  Fade,
  Skeleton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Payment as PaymentIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  AdminPanelSettings as AdminIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Image as ImageIcon,
  Shield as ShieldIcon,
  Business as BusinessIcon,
  Timer as TimerIcon,
  Lock as LockIcon,
  PeopleAlt as PeopleAltIcon,
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Public as PublicIcon,
  CalendarToday as CalendarTodayIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
  WorkspacePremium as PackageIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  Cancel as CancelIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Link as LinkIcon,
  ReceiptLong as ReceiptLongIcon,
  AttachMoney as MoneyIcon,
  Subscriptions as SubscriptionIcon,
  RocketLaunch as RocketLaunchIcon,
  Warning as WarningIcon,
  SettingsBackupRestore as SettingsBackupRestoreIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Storage as StorageIcon,
  DeleteForever as DeleteForeverIcon,
  Language as LanguageIcon,
  Euro as EuroIcon,
  Close as CloseIcon,
  FiberManualRecord as LiveIcon,
  AutoAwesome as AutoAwesomeIcon,
  Diamond as DiamondIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CheckCircleOutlineOutlined as CheckCircleOutlineIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import settingsService from '../../services/api/settingsService';
import PlatformServicesSettings from './PlatformServicesSettings';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, addDoc, deleteDoc, writeBatch, setDoc, query, where } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import { manualVerifyUserEmail, sendProfessionalPasswordResetEmail, sendProfessionalVerificationEmail } from '../../services/api/platformIntegrationService';
import { friendlyError } from '../../utils/userMessages';

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */

const palette = {
    canvas: '#F2F7F9',
    canvasDeep: '#E7F0F4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#405166',
    inkLight: '#5F7184',
    inkFaint: '#8999A9',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.055)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: 'var(--primary, #2E6F8E)',

    amber: '#D78B28',
    amberGlow: '#F1B75F',

    pine: '#2E8B69',
    pineGlow: '#69B99A',

    teal: '#268AA3',
    tealGlow: '#64B8C9',

    plum: '#8D5AC8',
    plumGlow: '#B78BDF',

    indigo: '#596BCE',
    indigoGlow: '#8796E2',

    success: '#1C8A56',
    warning: '#D18A0E',
    danger: '#C9342B',

    coral: '#E87461',
    sage: '#8BA888',
    lavender: '#9B7BB8',
    honey: '#E8C87A',
    ocean: '#4A8B9B',
};

const ACCENTS = [palette.rust, palette.teal, palette.amber, palette.pine, palette.plum, palette.indigo, palette.coral, palette.sage, palette.lavender];

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

/* ═══════════════════════════════════════════════════════════════════════════
   2 · HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const withTimeout = (promise, ms, message) => new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    Promise.resolve(promise).then(
        (value) => { window.clearTimeout(timer); resolve(value); },
        (error) => { window.clearTimeout(timer); reject(error); }
    );
});

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const liquid = (accent = palette.rust, { depth = 1, radius = 26 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.82)} 0%, ${rgba('#FFFFFF', 0.55)} 42%, ${rgba(accent, 0.07)} 100%)`,
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
    boxShadow: [
        `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}`,
        `inset 0 -1px 0 ${rgba('#FFFFFF', 0.35)}`,
        `inset 0 0 0 1px ${rgba(accent, 0.06)}`,
        `0 ${2 * depth}px ${6 * depth}px ${rgba(palette.ink, 0.04)}`,
        `0 ${10 * depth}px ${30 * depth}px -${8 * depth}px ${rgba(accent, 0.16)}`,
    ].join(', '),
    overflow: 'hidden',
    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1), border-color .4s ease',
});

const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .45s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), ${rgba('#FFFFFF', 0.75)}, ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
});

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID BACKDROP
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 25 }, (_, i) => ({
                id: i,
                size: 2 + ((i * 3) % 6),
                x: (i * 37) % 100,
                y: (i * 53) % 100,
                duration: 20 + ((i * 4) % 15),
                delay: (i * 2.1) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <motion.div
                animate={{ x: [0, -50, 35, 0], y: [0, 35, -25, 0], scale: [1, 1.08, 0.95, 1] }}
                transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-10%', left: '-3%',
                    width: 550, height: 550, borderRadius: '50%',
                    background: `radial-gradient(circle at 40% 40%, ${rgba(palette.rustGlow, 0.2)}, ${rgba(palette.rust, 0.08)} 48%, transparent 72%)`,
                    filter: 'blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: [0, 55, -30, 0], y: [0, -35, 25, 0], scale: [1, 0.92, 1.08, 1] }}
                transition={{ duration: 33, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-8%', right: '-4%',
                    width: 500, height: 500, borderRadius: '50%',
                    background: `radial-gradient(circle at 55% 45%, ${rgba(palette.plumGlow, 0.18)}, ${rgba(palette.teal, 0.08)} 50%, transparent 72%)`,
                    filter: 'blur(50px)',
                }}
            />
            <motion.div
                animate={{ x: ['-50%', '-54%', '-46%', '-50%'], y: ['-50%', '-46%', '-54%', '-50%'], scale: [1, 1.05, 0.97, 1] }}
                transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '45%', left: '50%',
                    width: 650, height: 650, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.amberGlow, 0.12)}, transparent 65%)`,
                    filter: 'blur(70px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 35}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 10}%`, `${m.x}%`],
                        opacity: [0, 0.5, 0],
                        scale: [0.4, 1.2, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.4)}`,
                    }}
                />
            ))}

            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.3,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.02)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.02)} 1px, transparent 1px)`,
                    backgroundSize: '60px 60px',
                    maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 75%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 75%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   4 · GLASS PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = false,
    padding = { xs: 2.25, md: 2.75 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::after': sheenLayer(accent),
                    transform: isHovered ? 'translateY(-4px) scale(1.005)' : 'translateY(0) scale(1)',
                    transition: 'box-shadow .6s cubic-bezier(.22,1,.36,1), transform .6s cubic-bezier(.22,1,.36,1)',
                    ...(lift && {
                        '&:hover': {
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 26px 56px -18px ${rgba(accent, 0.34)}`,
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const PanelHead = ({ icon, title, subtitle, accent = palette.rust, chip, right, dense = false }) => (
    <Box
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1.5, flexWrap: 'wrap', mb: dense ? 1.75 : 2.5,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 36 : 42} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontDisplay, fontWeight: 600,
                        fontSize: dense ? '1.02rem' : '1.14rem',
                        color: palette.ink, letterSpacing: '-0.015em', lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography sx={{ fontSize: '0.76rem', color: palette.inkLight, mt: 0.25 }}>{subtitle}</Typography>
                ) : (
                    <Box sx={{ width: dense ? 34 : 44, height: 3, mt: 0.75, borderRadius: 3, background: `linear-gradient(90deg, ${accent}, ${rgba(accent, 0)})` }} />
                )}
            </Box>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 24, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: accent, bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                    }}
                />
            )}
        </Box>
        {right}
    </Box>
);

const StatCard = ({ label, value, icon, accent, delta, hint, delay = 0, loading }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const displayValue = typeof value === 'number' ? compact(value) : value;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent, { radius: 24 }),
                    height: '100%', p: { xs: 2, md: 2.4 },
                    display: 'flex', flexDirection: 'column', minWidth: 0,
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-7px) scale(1.012)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 28px 60px -20px ${rgba(accent, 0.42)}`,
                    },
                    '&:hover .tf-orb': { opacity: 0.4, transform: 'scale(1.25)' },
                }}
            >
                <Box
                    className="tf-orb"
                    sx={{
                        position: 'absolute', top: -70, right: -70, width: 170, height: 170,
                        borderRadius: '50%', filter: 'blur(38px)', opacity: 0.16,
                        background: `radial-gradient(circle, ${accent}, transparent 68%)`,
                        transition: 'opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)',
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
                        <GlassIcon icon={icon} color={accent} size={40} />
                        {delta && (
                            <Chip
                                size="small"
                                icon={delta.trend === 'down' ? <TrendingDownIcon sx={{ fontSize: 13 }} /> : <TrendingUpIcon sx={{ fontSize: 13 }} />}
                                label={delta.label}
                                sx={{
                                    height: 22, fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700,
                                    color: delta.trend === 'up' ? palette.success : palette.danger,
                                    bgcolor: rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.09),
                                    border: `1px solid ${rgba(delta.trend === 'up' ? palette.success : palette.danger, 0.18)}`,
                                    '& .MuiChip-icon': { color: delta.trend === 'up' ? palette.success : palette.danger, ml: '6px' },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={40} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay, fontWeight: 600,
                                fontSize: { xs: '1.85rem', md: '2.2rem' },
                                lineHeight: 1, letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${accent})`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {displayValue}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.9, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.12em', color: palette.inkSoft,
                        }}
                    >
                        {label}
                    </Typography>
                    {hint && (
                        <Typography sx={{ mt: 'auto', pt: 1.5, fontSize: '0.68rem', color: palette.inkLight, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                component="span"
                                sx={{
                                    width: 6, height: 6, borderRadius: '50%', bgcolor: accent,
                                    boxShadow: `0 0 0 3px ${rgba(accent, 0.16)}`,
                                    animation: 'tfPulse 2.4s ease-in-out infinite',
                                }}
                            />
                            {hint}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </motion.div>
    );
};

const MetaPill = ({ label, tone = palette.inkLight, pulse = false, icon }) => (
    <Box
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.55,
            px: 1, py: 0.35, borderRadius: '9px', whiteSpace: 'nowrap',
            fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: tone, bgcolor: rgba(tone, 0.1), border: `1px solid ${rgba(tone, 0.2)}`,
        }}
    >
        {icon
            ? React.cloneElement(icon, { sx: { fontSize: 12 } })
            : <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tone, animation: pulse ? 'tfPulse 2.4s ease-in-out infinite' : 'none' }} />}
        {label}
    </Box>
);

const EmptyState = ({ icon, title, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1.5 }}>
            <GlassIcon icon={icon} color={palette.inkFaint} size={54} />
        </Box>
        {title && (
            <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.05rem', color: palette.ink }}>
                {title}
            </Typography>
        )}
        <Typography sx={{ color: palette.inkLight, fontSize: '0.85rem', mt: 0.5 }}>{text}</Typography>
        {action && (
            <Button
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 2.25, textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.5, py: 1,
                    color: '#fff',
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.3)}, 0 14px 30px -14px ${rgba(palette.rust, 1)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   5 · GLASS INPUT COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

const GlassInput = ({ label, icon, color = palette.rust, ...props }) => {
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
            <TextField
                fullWidth
                label={label}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '14px',
                        fontSize: '0.875rem',
                        background: focused ? rgba('#FFFFFF', 0.85) : hovered ? rgba('#FFFFFF', 0.65) : rgba('#FFFFFF', 0.45),
                        backdropFilter: 'blur(12px)',
                        transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                        '& fieldset': {
                            borderColor: focused ? color : hovered ? rgba(color, 0.4) : palette.hairline,
                            borderWidth: '1.5px',
                            transition: 'border-color .3s ease',
                        },
                        '&:hover fieldset': { borderColor: rgba(color, 0.5) },
                        '&.Mui-focused fieldset': { borderColor: color, borderWidth: '2px' },
                        '&.Mui-focused': {
                            boxShadow: `0 0 0 4px ${rgba(color, 0.12)}, 0 8px 32px ${rgba(color, 0.10)}`,
                            transform: 'translateY(-2px)',
                            background: rgba('#FFFFFF', 0.92),
                        },
                        '&:hover': {
                            transform: 'translateY(-1px)',
                            background: rgba('#FFFFFF', 0.72),
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: focused ? color : hovered ? palette.inkSoft : palette.inkLight,
                        fontFamily: fontBody,
                        fontWeight: 500,
                        transition: 'color .3s ease',
                        '&.Mui-focused': { color },
                    },
                    '& .MuiInputBase-input': {
                        fontFamily: fontBody,
                    },
                }}
                slotProps={{ input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <motion.div
                                animate={{
                                    scale: focused ? 1.15 : hovered ? 1.05 : 1,
                                    color: focused ? color : hovered ? palette.inkSoft : palette.inkLight,
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                {icon}
                            </motion.div>
                        </InputAdornment>
                    ),
                    ...props.InputProps,
                } }}
                {...props}
            />
        </motion.div>
    );
};

const GlassSelect = ({ label, options, value, onChange, color = palette.rust, ...props }) => {
    const [focused, setFocused] = useState(false);

    return (
        <FormControl fullWidth>
            <InputLabel sx={{ 
                color: focused ? color : palette.inkSoft, 
                fontFamily: fontBody, 
                fontWeight: 500,
                '&.Mui-focused': { color },
            }}>
                {label}
            </InputLabel>
            <Select
                value={value}
                onChange={onChange}
                label={label}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                sx={{
                    borderRadius: '14px',
                    fontSize: '0.875rem',
                    background: focused ? rgba('#FFFFFF', 0.85) : rgba('#FFFFFF', 0.5),
                    backdropFilter: 'blur(12px)',
                    transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                    '& fieldset': {
                        borderColor: focused ? color : palette.hairline,
                        borderWidth: '1.5px',
                        transition: 'border-color .3s ease',
                    },
                    '&:hover fieldset': { borderColor: rgba(color, 0.5) },
                    '&.Mui-focused fieldset': { borderColor: color, borderWidth: '2px' },
                    '&.Mui-focused': {
                        boxShadow: `0 0 0 4px ${rgba(color, 0.12)}, 0 8px 32px ${rgba(color, 0.10)}`,
                        transform: 'translateY(-2px)',
                        background: rgba('#FFFFFF', 0.92),
                    },
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        background: rgba('#FFFFFF', 0.72),
                    },
                    '& .MuiSelect-select': {
                        fontFamily: fontBody,
                        fontSize: '0.875rem',
                        color: palette.ink,
                    },
                    '& .MuiSelect-icon': {
                        color: focused ? color : palette.inkLight,
                    },
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            ...liquid(color, { radius: 16 }),
                            mt: 1,
                            maxHeight: 300,
                        }
                    }
                }}
                {...props}
            >
                {options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: fontBody }}>
                        {opt.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   6 · TAB PANEL
   ═══════════════════════════════════════════════════════════════════════════ */

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} style={{ width: '100%' }}>
        {value === index && <Box sx={{ py: 3, width: '100%' }}>{children}</Box>}
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   7 · ANIMATED SECTION
   ═══════════════════════════════════════════════════════════════════════════ */

const AnimatedSection = ({ children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
        style={{ height: '100%', width: '100%' }}
    >
        {children}
    </motion.div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   8 · SETTINGS SUB-COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════ */

// ── GENERAL SETTINGS ──
const GeneralSettingsComponent = ({ settings, setSettings, logoPreview, handleLogoChange, uploadingLogo, saving, handleSave }) => {
    const currencies = [
        { value: 'EUR', label: '🇪🇺 EUR - Euro' },
        { value: 'USD', label: '🇺🇸 USD - US Dollar' },
        { value: 'GBP', label: '🇬🇧 GBP - British Pound' },
        { value: 'INR', label: '🇮🇳 INR - Indian Rupee' },
    ];

    return (
        <Box>
            <PanelHead icon={<SettingsIcon />} title="General Settings" chip="Core Platform" accent={palette.rust} />
            <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />
            
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                <GlassPanel accent={palette.rust} delay={0.1} padding={{ xs: 2, md: 2.5 }}>
                    <GlassInput
                        label="Platform Name"
                        value={settings.platformName || ''}
                        onChange={(e) => setSettings({ ...settings, platformName: e.target.value })}
                        icon={<BusinessIcon sx={{ color: palette.rust }} />}
                        placeholder="Enter platform name"
                        color={palette.rust}
                    />
                </GlassPanel>

                <GlassPanel accent={palette.amber} delay={0.15} padding={{ xs: 2, md: 2.5 }}>
                    <GlassSelect
                        label="Platform Currency"
                        value={settings.currency || 'EUR'}
                        onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                        options={currencies}
                        color={palette.amber}
                    />
                    <Typography variant="caption" sx={{ color: palette.inkLight, mt: 1, display: 'block' }}>
                        <EuroIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                        Default currency for all transactions
                    </Typography>
                </GlassPanel>

                <GlassPanel accent={palette.teal} delay={0.2} padding={{ xs: 2, md: 2.5 }}>
                    <GlassSelect
                        label="Default Language"
                        value={settings.defaultLanguage || 'en'}
                        onChange={(e) => setSettings({ ...settings, defaultLanguage: e.target.value })}
                        options={[
                            { value: 'en', label: '🇬🇧 English' },
                            { value: 'es', label: '🇪🇸 Spanish' },
                            { value: 'fr', label: '🇫🇷 French' },
                            { value: 'pt', label: '🇵🇹 Portuguese' },
                            { value: 'ar', label: '🇦🇪 Arabic' },
                        ]}
                        color={palette.teal}
                    />
                </GlassPanel>

                <GlassPanel accent={palette.indigo} delay={0.25} padding={{ xs: 2, md: 2.5 }}>
                    <GlassSelect
                        label="Timezone"
                        value={settings.timezone || 'UTC'}
                        onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                        options={[
                            { value: 'UTC', label: '🌐 UTC' },
                            { value: 'America/New_York', label: '🗽 Eastern Time' },
                            { value: 'Europe/London', label: '🇬🇧 London' },
                            { value: 'Asia/Dubai', label: '🇦🇪 Dubai' },
                            { value: 'Asia/Tokyo', label: '🇯🇵 Tokyo' },
                        ]}
                        color={palette.indigo}
                    />
                </GlassPanel>

                <GlassPanel accent={palette.amber} delay={0.3} padding={{ xs: 2, md: 2.5 }} sx={{ gridColumn: { md: '1 / -1' } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, color: palette.ink }}>
                        <ImageIcon sx={{ color: palette.amber }} /> Platform Logo
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                        <Box sx={{
                            width: 120, height: 60, borderRadius: '12px',
                            border: `2px dashed ${palette.hairlineStrong}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.5)',
                        }}>
                            {logoPreview || settings.logo ? (
                                <img src={logoPreview || settings.logo} alt="Logo" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />
                            ) : (
                                <ImageIcon sx={{ color: palette.inkFaint, fontSize: 32 }} />
                            )}
                        </Box>
                        <Box>
                            <Button
                                variant="contained"
                                component="label"
                                disableElevation
                                startIcon={uploadingLogo ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                                disabled={uploadingLogo}
                                sx={{
                                    borderRadius: '12px', textTransform: 'none', fontWeight: 600,
                                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                    boxShadow: `0 8px 20px -6px ${rgba(palette.rust, 0.5)}`,
                                    '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.rust, 0.6)}` },
                                }}
                            >
                                {uploadingLogo ? 'Uploading...' : 'Choose Logo'}
                                <input type="file" accept=".png,.jpg,.jpeg,.webp" hidden onChange={handleLogoChange} />
                            </Button>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: palette.inkLight }}>
                                PNG, JPG or WEBP (max 5MB). Choose a file, then Save Changes to upload it.
                            </Typography>
                        </Box>
                    </Box>
                </GlassPanel>

                <GlassPanel accent={palette.plum} delay={0.33} padding={{ xs: 2, md: 2.5 }} sx={{ gridColumn: { md: '1 / -1' } }}>
                    <Typography sx={{ fontWeight: 800, color: palette.ink, mb: .7 }}>Platform Theme</Typography>
                    <Typography sx={{ fontSize: '.74rem', color: palette.inkSoft, mb: 2 }}>These colours are authoritative across both the Admin Portal and Company Portal.</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 1.5 }}>
                        {[['primaryColor','Primary'],['secondaryColor','Secondary'],['accentColor','Accent']].map(([field,label]) => (
                            <Box key={field} sx={{ display:'flex',alignItems:'center',gap:1.2,p:1.2,borderRadius:2.5,bgcolor:'#F7FAFC',border:`1px solid ${palette.hairline}` }}>
                                <Box component="input" type="color" value={settings[field] || '#2E6F8E'} onChange={(e)=>setSettings({...settings,[field]:e.target.value.toUpperCase()})} sx={{width:42,height:42,p:0,border:0,bgcolor:'transparent',cursor:'pointer'}} />
                                <Box><Typography sx={{fontSize:'.68rem',fontWeight:800,color:palette.inkLight}}>{label}</Typography><Typography sx={{fontSize:'.8rem',fontWeight:800,color:palette.ink}}>{settings[field]}</Typography></Box>
                            </Box>
                        ))}
                    </Box>
                </GlassPanel>

                <GlassPanel accent={palette.warning} delay={0.35} padding={{ xs: 2, md: 2.5 }} sx={{ gridColumn: { md: '1 / -1' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontWeight: 600, color: palette.ink, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningIcon sx={{ color: palette.warning }} /> Maintenance Mode
                            </Typography>
                            <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 0.5 }}>
                                Enable to restrict access. Only Super Admins can log in.
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.maintenanceMode || false}
                                    onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: palette.warning },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.warning },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="subtitle2" fontWeight={700} color={settings.maintenanceMode ? palette.warning : palette.inkSoft}>
                                    {settings.maintenanceMode ? 'ACTIVE' : 'OFF'}
                                </Typography>
                            }
                            sx={{ m: 0, flexDirection: 'column-reverse', gap: 0.5 }}
                        />
                    </Box>
                </GlassPanel>
            </Box>

            <Box sx={{ mt: 4 }}>
                <Button
                    variant="contained"
                    disableElevation
                    size="large"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    fullWidth
                    sx={{
                        borderRadius: '14px', textTransform: 'none', fontWeight: 700, py: 1.5,
                        background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                        boxShadow: `0 12px 30px -8px ${rgba(palette.rust, 0.7)}`,
                        '&:hover': { boxShadow: `0 16px 40px -8px ${rgba(palette.rust, 0.8)}` },
                    }}
                >
                    {saving ? 'Saving Platform Configuration...' : 'Save General Settings'}
                </Button>
            </Box>
        </Box>
    );
};

// ── SECURITY SETTINGS ──
const SecuritySettingsComponent = ({ settings, setSettings, saving, handleSave }) => (
    <Box>
        <PanelHead icon={<SecurityIcon />} title="Security Settings" chip="Protection" accent={palette.pine} />
        <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: '14px' }}>
            Password policy is enforced by TELCOFAIR registration. Two-factor authentication, authoritative login lockout and idle-session termination must be enforced by Firebase Authentication or a secured backend; the browser cannot safely provide those controls on its own.
        </Alert>
        
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <GlassPanel accent={palette.pine} delay={0.1} padding={{ xs: 2, md: 2.5 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.twoFactorRequired || false}
                            disabled
                            onChange={(e) => setSettings({ ...settings, twoFactorRequired: e.target.checked })}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                            }}
                        />
                    }
                    label="Require Two-Factor Authentication"
                />
            </GlassPanel>

            <GlassPanel accent={palette.amber} delay={0.15} padding={{ xs: 2, md: 2.5 }}>
                <GlassInput
                    label="Session Timeout (minutes)"
                    type="number"
                    disabled
                    value={settings.sessionTimeout || 60}
                    onChange={(e) => setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) || 60 })}
                    icon={<TimerIcon sx={{ color: palette.amber }} />}
                    helperText="Requires backend/Firebase session enforcement"
                    color={palette.amber}
                />
            </GlassPanel>

            <GlassPanel accent={palette.indigo} delay={0.2} padding={{ xs: 2, md: 2.5 }}>
                <GlassInput
                    label="Max Login Attempts"
                    type="number"
                    disabled
                    value={settings.maxLoginAttempts || 5}
                    onChange={(e) => setSettings({ ...settings, maxLoginAttempts: parseInt(e.target.value) || 5 })}
                    icon={<LockIcon sx={{ color: palette.indigo }} />}
                    helperText="Requires authoritative backend/Firebase Auth enforcement"
                    color={palette.indigo}
                    sx={{ mb: 2 }}
                />
                <GlassInput
                    label="Lockout Duration (minutes)"
                    type="number"
                    disabled
                    value={settings.lockoutDuration || 30}
                    onChange={(e) => setSettings({ ...settings, lockoutDuration: parseInt(e.target.value) || 30 })}
                    icon={<LockIcon sx={{ color: palette.indigo }} />}
                    color={palette.indigo}
                />
            </GlassPanel>

            <GlassPanel accent={palette.pine} delay={0.25} padding={{ xs: 2, md: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: palette.ink }}>
                    <SecurityIcon sx={{ color: palette.pine, mr: 1 }} /> Password Policy
                </Typography>
                <GlassInput
                    label="Minimum Password Length"
                    type="number"
                    value={settings.passwordMinLength || 8}
                    onChange={(e) => setSettings({ ...settings, passwordMinLength: parseInt(e.target.value) || 8 })}
                    color={palette.pine}
                    sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={settings.requireUppercase !== false}
                                onChange={(e) => setSettings({ ...settings, requireUppercase: e.target.checked })}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                                }}
                            />
                        }
                        label={<Typography variant="caption" sx={{ fontWeight: 500 }}>Uppercase</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={settings.requireLowercase !== false}
                                onChange={(e) => setSettings({ ...settings, requireLowercase: e.target.checked })}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                                }}
                            />
                        }
                        label={<Typography variant="caption" sx={{ fontWeight: 500 }}>Lowercase</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={settings.requireNumbers !== false}
                                onChange={(e) => setSettings({ ...settings, requireNumbers: e.target.checked })}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                                }}
                            />
                        }
                        label={<Typography variant="caption" sx={{ fontWeight: 500 }}>Numbers</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={settings.requireSpecialChars !== false}
                                onChange={(e) => setSettings({ ...settings, requireSpecialChars: e.target.checked })}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                                }}
                            />
                        }
                        label={<Typography variant="caption" sx={{ fontWeight: 500 }}>Special Characters</Typography>}
                    />
                </Box>
            </GlassPanel>
        </Box>

        <Box sx={{ mt: 4 }}>
            <Button
                variant="contained"
                disableElevation
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                fullWidth
                sx={{
                    borderRadius: '14px', textTransform: 'none', fontWeight: 700, py: 1.5,
                    background: `linear-gradient(140deg, ${palette.pine}, ${palette.pineGlow})`,
                    boxShadow: `0 12px 30px -8px ${rgba(palette.pine, 0.7)}`,
                    '&:hover': { boxShadow: `0 16px 40px -8px ${rgba(palette.pine, 0.8)}` },
                }}
            >
                {saving ? 'Saving Security Settings...' : 'Save Security Settings'}
            </Button>
        </Box>
    </Box>
);

// ── EMAIL SETTINGS ──
const EmailSettingsComponent = ({ settings, setSettings, saving, handleSave }) => (
    <Box>
        <PanelHead icon={<EmailIcon />} title="Email Settings" chip="SMTP" accent={palette.amber} />
        <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />
        
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <GlassPanel accent={palette.amber} delay={0.1} padding={{ xs: 2, md: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: palette.ink }}>
                    <SettingsIcon sx={{ color: palette.amber, mr: 1 }} /> SMTP Configuration
                </Typography>
                <GlassInput
                    label="SMTP Host"
                    value={settings.smtpHost || ''}
                    onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })}
                    color={palette.amber}
                    sx={{ mb: 2 }}
                />
                <GlassInput
                    label="SMTP Port"
                    type="number"
                    value={settings.smtpPort || 587}
                    onChange={(e) => setSettings({ ...settings, smtpPort: parseInt(e.target.value) || 587 })}
                    color={palette.amber}
                    sx={{ mb: 2 }}
                />
                <GlassInput
                    label="SMTP Username"
                    value={settings.smtpUser || ''}
                    onChange={(e) => setSettings({ ...settings, smtpUser: e.target.value })}
                    color={palette.amber}
                    sx={{ mb: 2 }}
                />
                <Alert severity="info" sx={{ borderRadius: '12px', mt: 1 }}>
                    SMTP passwords are no longer stored in browser-readable Firestore. Configure the private credential through the secured Platform Services backend before enabling SMTP in production.
                </Alert>
            </GlassPanel>

            <GlassPanel accent={palette.amber} delay={0.15} padding={{ xs: 2, md: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: palette.ink }}>
                    <EmailIcon sx={{ color: palette.rust, mr: 1 }} /> Sender Information
                </Typography>
                <GlassInput
                    label="From Email"
                    value={settings.fromEmail || ''}
                    onChange={(e) => setSettings({ ...settings, fromEmail: e.target.value })}
                    helperText="Email address emails will be sent from"
                    color={palette.amber}
                    sx={{ mb: 2 }}
                />
                <GlassInput
                    label="From Name"
                    value={settings.fromName || ''}
                    onChange={(e) => setSettings({ ...settings, fromName: e.target.value })}
                    helperText="Name displayed as the sender"
                    color={palette.amber}
                    sx={{ mb: 2 }}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.smtpSecure !== false}
                            onChange={(e) => setSettings({ ...settings, smtpSecure: e.target.checked })}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                            }}
                        />
                    }
                    label="Use Secure Connection (SSL/TLS)"
                    sx={{ mt: 2 }}
                />
            </GlassPanel>
        </Box>

        <Box sx={{ mt: 4 }}>
            <Button
                variant="contained"
                disableElevation
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                fullWidth
                sx={{
                    borderRadius: '14px', textTransform: 'none', fontWeight: 700, py: 1.5,
                    background: `linear-gradient(140deg, ${palette.amber}, ${palette.amberGlow})`,
                    boxShadow: `0 12px 30px -8px ${rgba(palette.amber, 0.7)}`,
                    '&:hover': { boxShadow: `0 16px 40px -8px ${rgba(palette.amber, 0.8)}` },
                }}
            >
                {saving ? 'Saving Email Settings...' : 'Save Email Settings'}
            </Button>
        </Box>
    </Box>
);

// ── PAYMENT SETTINGS (FULLY COMPLETE) ──

const PaymentSettingsComponent = ({ 
    settings, setSettings, saving, handleSave, testConnection, testingConnection, 
    gatewayConfigs, handleAddGateway, handleSaveGateway, handleDeleteGateway, 
    handleEditGateway, openGatewayDialog, setOpenGatewayDialog, editingGateway, 
    setEditingGateway, gatewayForm, setGatewayForm, selectedGateway, 
    setSelectedGateway, handleGatewayChange
}) => {
    const gatewayProviders = [
        { value: 'stripe', label: '💳 Stripe', color: '#635BFF' },
        { value: 'paypal', label: '💰 PayPal', color: '#003087' },
        { value: 'revolut', label: '🏦 Revolut', color: '#0d5c7a' },
        { value: 'bank_transfer', label: '🏦 Bank Transfer', color: '#2d7d46' },
        { value: 'offline', label: '📝 Offline Payment', color: '#8B7355' },
    ];

    const getGatewayIcon = (provider) => {
        const icons = { 
            stripe: <CreditCardIcon />, 
            paypal: <PaymentIcon />, 
            revolut: <AccountBalanceIcon />, 
            bank_transfer: <AccountBalanceIcon />, 
            offline: <ReceiptLongIcon /> 
        };
        return icons[provider] || <PaymentIcon />;
    };

    const getGatewayColor = (provider) => {
        const colors = { 
            stripe: '#635BFF', 
            paypal: '#003087', 
            revolut: '#0d5c7a', 
            bank_transfer: '#2d7d46', 
            offline: '#8B7355' 
        };
        return colors[provider] || palette.rust;
    };

    const getGatewayStatusColor = (status) => {
        switch(status) {
            case 'active': return palette.pine;
            case 'inactive': return palette.inkSoft;
            case 'error': return palette.danger;
            default: return palette.warning;
        }
    };

    return (
        <Box>
            <PanelHead icon={<PaymentIcon />} title="Payment Gateway Configuration" chip={`${gatewayConfigs?.length || 0} Gateways`} accent={palette.teal} />
            <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />
            
            {selectedGateway && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: '12px', bgcolor: rgba(getGatewayColor(selectedGateway.provider), 0.06), border: `1px solid ${rgba(getGatewayColor(selectedGateway.provider), 0.2)}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: getGatewayColor(selectedGateway.provider), width: 36, height: 36 }}>
                                {getGatewayIcon(selectedGateway.provider)}
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle2" fontWeight={700}>
                                    Active Gateway: {gatewayProviders.find(p => p.value === selectedGateway.provider)?.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                    Status: <Chip 
                                        label={selectedGateway.status || 'Active'} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: `${getGatewayStatusColor(selectedGateway.status || 'active')}15`, 
                                            color: getGatewayStatusColor(selectedGateway.status || 'active'), 
                                            fontWeight: 600, 
                                            height: 20, 
                                            fontSize: '0.6rem' 
                                        }} 
                                    />
                                </Typography>
                            </Box>
                        </Box>
                        <Chip 
                            icon={selectedGateway.status === 'active' ? <CheckCircleIcon /> : <WarningIcon />} 
                            label={selectedGateway.status === 'active' ? 'Accepting Payments' : 'Not Active'} 
                            sx={{ 
                                bgcolor: selectedGateway.status === 'active' ? `${palette.pine}12` : `${palette.warning}12`, 
                                color: selectedGateway.status === 'active' ? palette.pine : palette.warning, 
                                fontWeight: 700 
                            }} 
                        />
                    </Box>
                </Alert>
            )}

            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
                {/* Gateway Selection */}
                <GlassPanel accent={palette.teal} delay={0.1} padding={{ xs: 2, md: 2.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: palette.ink }}>
                        <SettingsIcon sx={{ color: palette.gold }} /> Primary Payment Gateway
                    </Typography>
                    <GlassSelect 
                        label="Select Active Payment Gateway" 
                        value={selectedGateway?.id || ''} 
                        onChange={(e) => { 
                            const gateway = gatewayConfigs?.find(g => g.id === e.target.value); 
                            setSelectedGateway(gateway); 
                            handleGatewayChange(gateway); 
                        }} 
                        options={gatewayConfigs?.map(g => ({ 
                            value: g.id, 
                            label: `${gatewayProviders.find(p => p.value === g.provider)?.label} ${g.status === 'active' ? '✅' : '⏸️'}` 
                        })) || []} 
                        color={palette.teal}
                    />
                    {selectedGateway && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                                icon={<CheckCircleIcon />} 
                                label={`${selectedGateway.provider.charAt(0).toUpperCase() + selectedGateway.provider.slice(1)}`} 
                                sx={{ bgcolor: `${getGatewayColor(selectedGateway.provider)}15`, color: getGatewayColor(selectedGateway.provider) }} 
                            />
                            <Chip 
                                label={`${selectedGateway.currency || 'USD'}`} 
                                sx={{ bgcolor: `${palette.gold}15`, color: palette.gold }} 
                            />
                            <Chip 
                                label={`Mode: ${selectedGateway.mode || 'live'}`} 
                                sx={{ bgcolor: `${palette.rust}15`, color: palette.rust }} 
                            />
                        </Box>
                    )}
                </GlassPanel>

                {/* Quick Actions */}
                <GlassPanel accent={palette.teal} delay={0.15} padding={{ xs: 2, md: 2.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: palette.ink }}>
                        <AddIcon sx={{ color: palette.teal, mr: 1 }} /> Quick Actions
                    </Typography>
                    <Stack spacing={1.5}>
                        <Button 
                            variant="outlined" 
                            startIcon={<AddIcon />} 
                            onClick={() => { setEditingGateway(null); setGatewayForm({ provider: '', currency: settings.currency || 'EUR', mode: 'live', status: 'active' }); setOpenGatewayDialog(true); }}
                            fullWidth
                            sx={{ 
                                borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                                borderColor: palette.teal, color: palette.teal,
                                '&:hover': { borderColor: palette.teal, bgcolor: `${palette.teal}08` }
                            }}
                        >
                            Add New Gateway
                        </Button>
                        {selectedGateway && (
                            <Button 
                                variant="outlined" 
                                startIcon={<EditIcon />} 
                                onClick={() => handleEditGateway(selectedGateway)}
                                fullWidth
                                sx={{ 
                                    borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                                    borderColor: palette.amber, color: palette.amber,
                                    '&:hover': { borderColor: palette.amber, bgcolor: `${palette.amber}08` }
                                }}
                            >
                                Edit Current Gateway
                            </Button>
                        )}
                        {selectedGateway && (
                            <Button 
                                variant="outlined" 
                                startIcon={testingConnection ? <CircularProgress size={16} /> : <CheckIcon />} 
                                onClick={() => testConnection(selectedGateway.provider)} 
                                disabled={testingConnection}
                                fullWidth
                                sx={{ 
                                    borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                                    borderColor: getGatewayColor(selectedGateway.provider), 
                                    color: getGatewayColor(selectedGateway.provider),
                                    '&:hover': { borderColor: getGatewayColor(selectedGateway.provider), bgcolor: `${getGatewayColor(selectedGateway.provider)}08` }
                                }}
                            >
                                {testingConnection ? 'Testing...' : 'Test Connection'}
                            </Button>
                        )}
                    </Stack>
                </GlassPanel>

                {/* Gateway Details */}
                {selectedGateway && (
                    <>
                        <GlassPanel accent={getGatewayColor(selectedGateway.provider)} delay={0.2} padding={{ xs: 2, md: 2.5 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: palette.ink }}>
                                {getGatewayIcon(selectedGateway.provider)} Configuration Details
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600 }}>Provider</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: getGatewayColor(selectedGateway.provider) }}>
                                        {gatewayProviders.find(p => p.value === selectedGateway.provider)?.label}
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600 }}>Status</Typography>
                                    <Chip 
                                        label={selectedGateway.status || 'Active'} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: `${getGatewayStatusColor(selectedGateway.status || 'active')}15`, 
                                            color: getGatewayStatusColor(selectedGateway.status || 'active'), 
                                            fontWeight: 600 
                                        }} 
                                    />
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600 }}>Currency</Typography>
                                    <Typography variant="body2" fontWeight={600}>{selectedGateway.currency || 'USD'}</Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600 }}>Mode</Typography>
                                    <Chip 
                                        label={selectedGateway.mode || 'Live'} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: (selectedGateway.mode || 'live') === 'live' ? `${palette.pine}15` : `${palette.warning}15`, 
                                            color: (selectedGateway.mode || 'live') === 'live' ? palette.pine : palette.warning, 
                                            fontWeight: 600 
                                        }} 
                                    />
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600 }}>Fee (%)</Typography>
                                    <Typography variant="body2" fontWeight={600}>{selectedGateway.fee || 0}%</Typography>
                                </Box>
                                {selectedGateway.description && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600, display: 'block', mb: 0.5 }}>Description</Typography>
                                            <Typography variant="body2" sx={{ color: palette.inkSoft }}>{selectedGateway.description}</Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </GlassPanel>

                        <GlassPanel accent={getGatewayColor(selectedGateway.provider)} delay={0.25} padding={{ xs: 2, md: 2.5 }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: palette.ink }}>
                                <SecurityIcon sx={{ color: palette.pine }} /> API Credentials
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {selectedGateway.publicKey && (
                                    <>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600, display: 'block', mb: 0.5 }}>Public / Publishable Key</Typography>
                                            <TextField fullWidth value={selectedGateway.publicKey} size="small" InputProps={{ readOnly: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: palette.canvasAlt, fontFamily: fontMono, fontSize: '0.75rem' } }} />
                                        </Box>
                                        <Divider />
                                    </>
                                )}
                                <Alert severity="info" sx={{ borderRadius: 2, fontSize: '.72rem' }}>Private API keys and webhook secrets are not stored in this browser-facing gateway record. Configure secret credentials only through the secured server integration.</Alert>
                                {selectedGateway.bankDetails && (
                                    <>
                                        <Divider />
                                        <Box>
                                            <Typography variant="caption" sx={{ color: palette.inkSoft, fontWeight: 600, display: 'block', mb: 1 }}>Bank Account Details</Typography>
                                            <Box sx={{ 
                                                bgcolor: palette.canvasAlt, 
                                                p: 2, 
                                                borderRadius: '10px', 
                                                border: `1px solid ${palette.hairline}` 
                                            }}>
                                                <Typography variant="body2" sx={{ fontFamily: fontMono, fontSize: '0.75rem', color: palette.inkSoft, whiteSpace: 'pre-wrap' }}>
                                                    {selectedGateway.bankDetails.accountName && `Account Name: ${selectedGateway.bankDetails.accountName}\n`}
                                                    {selectedGateway.bankDetails.accountNumber && `Account Number: ${selectedGateway.bankDetails.accountNumber}\n`}
                                                    {selectedGateway.bankDetails.bankName && `Bank Name: ${selectedGateway.bankDetails.bankName}\n`}
                                                    {selectedGateway.bankDetails.routingNumber && `Routing Number: ${selectedGateway.bankDetails.routingNumber}\n`}
                                                    {selectedGateway.bankDetails.iban && `IBAN: ${selectedGateway.bankDetails.iban}\n`}
                                                    {selectedGateway.bankDetails.swift && `SWIFT/BIC: ${selectedGateway.bankDetails.swift}`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </GlassPanel>
                    </>
                )}

                {/* All Gateways List */}
                <GlassPanel accent={palette.teal} delay={0.3} padding={{ xs: 2, md: 2.5 }} sx={{ gridColumn: { lg: '1 / -1' } }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: palette.ink }}>
                        <PaymentIcon sx={{ color: palette.teal }} /> All Configured Gateways ({gatewayConfigs?.length || 0})
                    </Typography>
                    {gatewayConfigs?.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" sx={{ color: palette.inkSoft }}>No gateways configured. Click &quot;Add New Gateway&quot; to get started.</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2 }}>
                            {gatewayConfigs.map((gateway) => {
                                const provider = gatewayProviders.find(p => p.value === gateway.provider);
                                const isActive = selectedGateway?.id === gateway.id;
                                return (
                                    <Card 
                                        key={gateway.id} 
                                        sx={{ 
                                            borderRadius: '12px', 
                                            border: `1px solid ${isActive ? getGatewayColor(gateway.provider) : palette.hairline}`,
                                            bgcolor: isActive ? `${getGatewayColor(gateway.provider)}04` : 'rgba(255,255,255,0.5)',
                                            backdropFilter: 'blur(10px)',
                                            transition: 'all 0.3s ease',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            '&:hover': { 
                                                borderColor: getGatewayColor(gateway.provider), 
                                                boxShadow: `0 4px 16px ${getGatewayColor(gateway.provider)}15` 
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 2.5, flex: 1, '&:last-child': { pb: 2.5 } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar sx={{ bgcolor: getGatewayColor(gateway.provider), width: 36, height: 36 }}>
                                                        {getGatewayIcon(gateway.provider)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight={600}>
                                                            {provider?.label}
                                                            {isActive && (
                                                                <Chip 
                                                                    label="Active" 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        ml: 1, 
                                                                        bgcolor: `${palette.pine}15`, 
                                                                        color: palette.pine, 
                                                                        height: 18, 
                                                                        fontSize: '0.6rem' 
                                                                    }} 
                                                                />
                                                            )}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                            {gateway.currency || 'USD'} • {gateway.mode || 'live'} • Fee: {gateway.fee || 0}%
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Set as Active">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => { setSelectedGateway(gateway); handleGatewayChange(gateway); }} 
                                                            sx={{ 
                                                                color: isActive ? palette.pine : palette.inkSoft, 
                                                                '&:hover': { bgcolor: `${palette.pine}12` } 
                                                            }}
                                                        >
                                                            {isActive ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleEditGateway(gateway)} 
                                                            sx={{ color: palette.amber, '&:hover': { bgcolor: `${palette.amber}12` } }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleDeleteGateway(gateway.id)} 
                                                            sx={{ color: palette.danger, '&:hover': { bgcolor: `${palette.danger}12` } }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>
                    )}
                </GlassPanel>
            </Box>

            {/* Save Button */}
            <Box sx={{ mt: 4 }}>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    fullWidth
                    sx={{
                        borderRadius: '14px', textTransform: 'none', fontWeight: 700, py: 1.5,
                        background: `linear-gradient(140deg, ${palette.teal}, ${palette.tealGlow})`,
                        boxShadow: `0 12px 30px -8px ${rgba(palette.teal, 0.7)}`,
                        '&:hover': { boxShadow: `0 16px 40px -8px ${rgba(palette.teal, 0.8)}` },
                    }}
                >
                    {saving ? 'Saving Payment Settings...' : 'Save Payment Settings'}
                </Button>
            </Box>

            {/* Gateway Dialog */}
            <Dialog open={openGatewayDialog} onClose={() => { setOpenGatewayDialog(false); setEditingGateway(null); setGatewayForm({}); }} maxWidth="md" fullWidth>
                <GlassPanel accent={palette.teal} padding={{ xs: 2.5, md: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                            <PaymentIcon sx={{ color: palette.teal, mr: 1 }} /> {editingGateway ? 'Edit Gateway' : 'Add Payment Gateway'}
                        </Typography>
                        <IconButton onClick={() => { setOpenGatewayDialog(false); setEditingGateway(null); setGatewayForm({}); }} sx={{ color: palette.inkSoft }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <GlassSelect 
                                label="Gateway Provider" 
                                value={gatewayForm.provider || ''} 
                                onChange={(e) => setGatewayForm({ ...gatewayForm, provider: e.target.value })} 
                                options={gatewayProviders.map(p => ({ value: p.value, label: p.label }))} 
                                color={palette.teal}
                            />
                        </Box>
                        {gatewayForm.provider && (
                            <>
                                <GlassInput
                                    label="Currency"
                                    value={gatewayForm.currency || settings.currency || 'EUR'}
                                    onChange={(e) => setGatewayForm({ ...gatewayForm, currency: e.target.value })}
                                    icon={<MoneyIcon sx={{ color: palette.gold }} />}
                                    color={palette.teal}
                                />
                                <GlassSelect
                                    label="Mode"
                                    value={gatewayForm.mode || 'live'}
                                    onChange={(e) => setGatewayForm({ ...gatewayForm, mode: e.target.value })}
                                    options={[
                                        { value: 'live', label: '🔴 Live' },
                                        { value: 'test', label: '🧪 Test' },
                                        { value: 'sandbox', label: '📦 Sandbox' }
                                    ]}
                                    color={palette.teal}
                                />
                                <GlassInput
                                    label="Transaction Fee (%)"
                                    type="number"
                                    value={gatewayForm.fee || 0}
                                    onChange={(e) => setGatewayForm({ ...gatewayForm, fee: parseFloat(e.target.value) || 0 })}
                                    icon={<MoneyIcon sx={{ color: palette.gold }} />}
                                    helperText="Percentage fee charged per transaction"
                                    color={palette.teal}
                                    sx={{ gridColumn: { md: '1 / -1' } }}
                                />
                            </>
                        )}
                        {gatewayForm.provider && gatewayForm.provider !== 'bank_transfer' && gatewayForm.provider !== 'offline' && (
                            <>
                                <GlassInput
                                    label="Public / Publishable Key"
                                    value={gatewayForm.publicKey || ''}
                                    onChange={(e) => setGatewayForm({ ...gatewayForm, publicKey: e.target.value })}
                                    icon={<SecurityIcon sx={{ color: palette.pine }} />}
                                    helperText="Browser-safe public key only. Keep private keys and webhook secrets server-side."
                                    color={palette.teal}
                                    sx={{ gridColumn: '1 / -1' }}
                                />
                            </>
                        )}
                        {gatewayForm.provider === 'bank_transfer' && (
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <GlassPanel accent={palette.bank} hover={false} padding={{ xs: 2, md: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: palette.ink }}>
                                        <AccountBalanceIcon sx={{ color: palette.bank }} /> Bank Account Details
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                                        <GlassInput
                                            label="Account Name"
                                            value={gatewayForm.bankDetails?.accountName || ''}
                                            onChange={(e) => setGatewayForm({ ...gatewayForm, bankDetails: { ...gatewayForm.bankDetails, accountName: e.target.value } })}
                                            icon={<BusinessIcon sx={{ color: palette.bank }} />}
                                            color={palette.bank}
                                        />
                                        <GlassInput
                                            label="Account Number"
                                            value={gatewayForm.bankDetails?.accountNumber || ''}
                                            onChange={(e) => setGatewayForm({ ...gatewayForm, bankDetails: { ...gatewayForm.bankDetails, accountNumber: e.target.value } })}
                                            icon={<ReceiptIcon sx={{ color: palette.bank }} />}
                                            color={palette.bank}
                                        />
                                        <GlassInput
                                            label="Bank Name"
                                            value={gatewayForm.bankDetails?.bankName || ''}
                                            onChange={(e) => setGatewayForm({ ...gatewayForm, bankDetails: { ...gatewayForm.bankDetails, bankName: e.target.value } })}
                                            icon={<BusinessIcon sx={{ color: palette.bank }} />}
                                            color={palette.bank}
                                        />
                                        <GlassInput
                                            label="Routing Number / Sort Code"
                                            value={gatewayForm.bankDetails?.routingNumber || ''}
                                            onChange={(e) => setGatewayForm({ ...gatewayForm, bankDetails: { ...gatewayForm.bankDetails, routingNumber: e.target.value } })}
                                            icon={<AccountBalanceIcon sx={{ color: palette.bank }} />}
                                            color={palette.bank}
                                        />
                                        <GlassInput
                                            label="IBAN (International)"
                                            value={gatewayForm.bankDetails?.iban || ''}
                                            onChange={(e) => setGatewayForm({ ...gatewayForm, bankDetails: { ...gatewayForm.bankDetails, iban: e.target.value } })}
                                            icon={<PublicIcon sx={{ color: palette.bank }} />}
                                            color={palette.bank}
                                        />
                                        <GlassInput
                                            label="SWIFT / BIC Code"
                                            value={gatewayForm.bankDetails?.swift || ''}
                                            onChange={(e) => setGatewayForm({ ...gatewayForm, bankDetails: { ...gatewayForm.bankDetails, swift: e.target.value } })}
                                            icon={<PublicIcon sx={{ color: palette.bank }} />}
                                            color={palette.bank}
                                        />
                                    </Box>
                                </GlassPanel>
                            </Box>
                        )}
                        <GlassInput
                            label="Description (Optional)"
                            value={gatewayForm.description || ''}
                            onChange={(e) => setGatewayForm({ ...gatewayForm, description: e.target.value })}
                            multiline
                            rows={2}
                            icon={<BusinessIcon sx={{ color: palette.teal }} />}
                            placeholder="Brief description of this payment gateway"
                            color={palette.teal}
                            sx={{ gridColumn: '1 / -1' }}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, gridColumn: '1 / -1' }}>
                            <FormControlLabel 
                                control={
                                    <Switch 
                                        checked={gatewayForm.enableWebhooks !== false} 
                                        onChange={(e) => setGatewayForm({ ...gatewayForm, enableWebhooks: e.target.checked })} 
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine } }} 
                                    />
                                } 
                                label="Enable Webhooks" 
                            />
                            <FormControlLabel 
                                control={
                                    <Switch 
                                        checked={gatewayForm.enableRefunds !== false} 
                                        onChange={(e) => setGatewayForm({ ...gatewayForm, enableRefunds: e.target.checked })} 
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine } }} 
                                    />
                                } 
                                label="Enable Refunds" 
                            />
                            <FormControlLabel 
                                control={
                                    <Switch 
                                        checked={gatewayForm.enableRecurring || false} 
                                        onChange={(e) => setGatewayForm({ ...gatewayForm, enableRecurring: e.target.checked })} 
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: palette.gold }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.gold } }} 
                                    />
                                } 
                                label="Enable Recurring Payments" 
                            />
                            <FormControlLabel 
                                control={
                                    <Switch 
                                        checked={gatewayForm.status !== 'inactive'} 
                                        onChange={(e) => setGatewayForm({ ...gatewayForm, status: e.target.checked ? 'active' : 'inactive' })} 
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine } }} 
                                    />
                                } 
                                label="Active" 
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, mt: 3, justifyContent: 'flex-end' }}>
                        <Button 
                            onClick={() => { setOpenGatewayDialog(false); setEditingGateway(null); setGatewayForm({}); }} 
                            sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            disableElevation 
                            onClick={editingGateway ? () => handleSaveGateway(editingGateway.id) : handleAddGateway} 
                            startIcon={editingGateway ? <SaveIcon /> : <AddIcon />} 
                            disabled={!gatewayForm.provider}
                            sx={{
                                borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3, py: 1.1,
                                background: `linear-gradient(140deg, ${palette.teal}, ${palette.tealGlow})`,
                                boxShadow: `0 8px 20px -6px ${rgba(palette.teal, 0.5)}`,
                                '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.teal, 0.6)}` },
                                '&:disabled': { opacity: 0.5 }
                            }}
                        >
                            {editingGateway ? 'Update Gateway' : 'Add Gateway'}
                        </Button>
                    </Box>
                </GlassPanel>
            </Dialog>
        </Box>
    );
};

// ── SMS SETTINGS ──
const SmsSettingsComponent = ({ settings, setSettings, saving, handleSave, testConnection, testingConnection }) => (
    <Box>
        <PanelHead icon={<SmsIcon />} title="SMS Settings" chip="Twilio" accent={palette.indigo} />
        <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />
        
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <GlassPanel accent={palette.indigo} delay={0.1} padding={{ xs: 2, md: 2.5 }}>
                <GlassSelect
                    label="SMS Provider"
                    value={settings.provider || 'twilio'}
                    onChange={(e) => setSettings({ ...settings, provider: e.target.value })}
                    options={[
                        { value: 'twilio', label: '📱 Twilio' },
                        { value: 'africastalking', label: "🌍 Africa's Talking" },
                        { value: 'messagebird', label: '🐦 MessageBird' },
                    ]}
                    color={palette.indigo}
                />
            </GlassPanel>

            <GlassPanel accent={palette.indigo} delay={0.15} padding={{ xs: 2, md: 2.5 }}>
                <GlassInput
                    label="Default Country Code"
                    value={settings.defaultCountryCode || '+1'}
                    onChange={(e) => setSettings({ ...settings, defaultCountryCode: e.target.value })}
                    icon={<PublicIcon sx={{ color: palette.rust }} />}
                    placeholder="+1"
                    color={palette.indigo}
                />
            </GlassPanel>

            <GlassPanel accent={palette.indigo} delay={0.2} padding={{ xs: 2, md: 2.5 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.enableDeliveryReports !== false}
                            onChange={(e) => setSettings({ ...settings, enableDeliveryReports: e.target.checked })}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                            }}
                        />
                    }
                    label="Enable Delivery Reports"
                />
            </GlassPanel>

            <GlassPanel accent={palette.indigo} delay={0.25} padding={{ xs: 2, md: 2.5 }}>
                <GlassInput
                    label="Max Retries"
                    type="number"
                    value={settings.maxRetries || 3}
                    onChange={(e) => setSettings({ ...settings, maxRetries: parseInt(e.target.value) || 3 })}
                    icon={<SettingsIcon sx={{ color: palette.amber }} />}
                    color={palette.indigo}
                />
            </GlassPanel>
        </Box>

        <Box sx={{ mt: 4 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
                <Button
                    variant="outlined"
                    disableElevation
                    startIcon={testingConnection ? <CircularProgress size={20} /> : <CheckIcon />}
                    onClick={() => testConnection('SMS')}
                    disabled={testingConnection}
                    sx={{ 
                        flex: 1, borderRadius: '12px', textTransform: 'none', fontWeight: 600,
                        borderColor: palette.indigo, color: palette.indigo,
                        '&:hover': { borderColor: palette.indigo, bgcolor: `${palette.indigo}08` } 
                    }}
                >
                    {testingConnection ? 'Testing...' : 'Test Connection'}
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ 
                        flex: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 700, py: 1.5,
                        background: `linear-gradient(140deg, ${palette.indigo}, ${palette.indigoGlow})`,
                        boxShadow: `0 12px 30px -8px ${rgba(palette.indigo, 0.7)}`,
                        '&:hover': { boxShadow: `0 16px 40px -8px ${rgba(palette.indigo, 0.8)}` },
                    }}
                >
                    {saving ? 'Saving Changes...' : 'Save SMS Settings'}
                </Button>
            </Stack>
        </Box>
    </Box>
);

// ── WHATSAPP SETTINGS ──
const WhatsAppSettingsComponent = ({ settings, setSettings, saving, handleSave, testConnection, testingConnection }) => (
    <Box>
        <PanelHead icon={<WhatsAppIcon />} title="WhatsApp Settings" chip="Business" accent="#25D366" />
        <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />
        
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <GlassPanel accent="#25D366" delay={0.1} padding={{ xs: 2, md: 2.5 }}>
                <GlassSelect
                    label="WhatsApp Provider"
                    value={settings.provider || 'twilio'}
                    onChange={(e) => setSettings({ ...settings, provider: e.target.value })}
                    options={[
                        { value: 'twilio', label: '📱 Twilio' },
                        { value: 'meta', label: '👤 Meta (Facebook)' },
                        { value: 'ultramsg', label: '💬 UltraMsg' },
                    ]}
                    color="#25D366"
                />
            </GlassPanel>

            <GlassPanel accent="#25D366" delay={0.15} padding={{ xs: 2, md: 2.5 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.enableTemplates !== false}
                            onChange={(e) => setSettings({ ...settings, enableTemplates: e.target.checked })}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#25D366' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#25D366' },
                            }}
                        />
                    }
                    label="Enable Message Templates"
                />
            </GlassPanel>

            <GlassPanel accent="#25D366" delay={0.2} padding={{ xs: 2, md: 2.5 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.enableDeliveryReports !== false}
                            onChange={(e) => setSettings({ ...settings, enableDeliveryReports: e.target.checked })}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#25D366' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#25D366' },
                            }}
                        />
                    }
                    label="Enable Delivery Reports"
                />
            </GlassPanel>

            <GlassPanel accent="#25D366" delay={0.25} padding={{ xs: 2, md: 2.5 }}>
                <GlassInput
                    label="Max Retries"
                    type="number"
                    value={settings.maxRetries || 3}
                    onChange={(e) => setSettings({ ...settings, maxRetries: parseInt(e.target.value) || 3 })}
                    icon={<SettingsIcon sx={{ color: palette.amber }} />}
                    color="#25D366"
                />
            </GlassPanel>
        </Box>

        <Box sx={{ mt: 4 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
                <Button
                    variant="outlined"
                    disableElevation
                    startIcon={testingConnection ? <CircularProgress size={20} /> : <CheckIcon />}
                    onClick={() => testConnection('WhatsApp')}
                    disabled={testingConnection}
                    sx={{ 
                        flex: 1, borderRadius: '12px', textTransform: 'none', fontWeight: 600,
                        borderColor: '#25D366', color: '#25D366',
                        '&:hover': { borderColor: '#075E54', bgcolor: 'rgba(37,211,102,0.06)' } 
                    }}
                >
                    {testingConnection ? 'Testing...' : 'Test Connection'}
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ 
                        flex: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 700, py: 1.5,
                        background: `linear-gradient(140deg, #25D366, #075E54)`,
                        boxShadow: `0 12px 30px -8px #25D36670`,
                        '&:hover': { boxShadow: `0 16px 40px -8px #25D36680` },
                    }}
                >
                    {saving ? 'Saving Changes...' : 'Save WhatsApp Settings'}
                </Button>
            </Stack>
        </Box>
    </Box>
);

// ── SUBSCRIPTION SETTINGS ──
const SubscriptionSettingsComponent = ({ settings, setSettings, saving, handleSave, packages = [] }) => {
    const currencySymbol = settings.currency === 'EUR' ? '€' : '$';

    return (
        <Box>
            <PanelHead icon={<SubscriptionIcon />} title="Subscription & Billing" chip={settings.plan ? 'Active Plan' : 'No Plan'} accent={palette.plum} />
            <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />
            
            <Alert severity={settings.status === 'active' ? 'success' : 'warning'} sx={{ mb: 3, borderRadius: '12px' }}>
                <Typography variant="subtitle2" fontWeight={600}>
                    {settings.status === 'active' ? 'Subscription Active' : 'Subscription Inactive'}
                </Typography>
                <Typography variant="body2">
                    {settings.status === 'active' 
                        ? `Your selected plan is active. Next billing date: ${settings.nextBillingDate || 'N/A'}` 
                        : 'Please select a plan to activate your subscription'}
                </Typography>
            </Alert>

            {packages.length === 0 ? (
                <GlassPanel accent={palette.inkLight} hover={false} sx={{ textAlign: 'center', py: 4 }}>
                    <PackageIcon sx={{ fontSize: 48, color: palette.inkLight, opacity: 0.3, mb: 1.5 }} />
                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, color: palette.inkSoft, fontSize: '1.05rem' }}>
                        No Packages Available
                    </Typography>
                    <Typography sx={{ fontFamily: fontBody, color: palette.inkLight, fontSize: '0.875rem' }}>
                        Please create packages in the &quot;Packages&quot; tab first.
                    </Typography>
                </GlassPanel>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 3, mb: 3 }}>
                    {packages.filter(p => p.isActive).map((pkg, index) => {
                        const isSelected = settings.plan === pkg.id;
                        const colors = [palette.rust, palette.pine, palette.teal, palette.gold, palette.plum];
                        const color = colors[index % colors.length];
                        return (
                            <Card 
                                key={pkg.id} 
                                elevation={0} 
                                sx={{ 
                                    borderRadius: '16px', 
                                    border: `2px solid ${isSelected ? color : palette.hairline}`,
                                    boxShadow: isSelected ? `0 8px 32px ${rgba(color, 0.25)}` : '0 1px 2px rgba(33,26,20,0.04)',
                                    transition: 'all 0.3s ease',
                                    height: '100%',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(255,255,255,0.6)',
                                    backdropFilter: 'blur(10px)',
                                    '&:hover': { 
                                        transform: 'translateY(-4px)',
                                        boxShadow: isSelected ? `0 12px 48px ${rgba(color, 0.35)}` : '0 8px 32px rgba(33,26,20,0.08)'
                                    }
                                }}
                                onClick={() => { 
                                    setSettings({ ...settings, plan: pkg.id }); 
                                    toast.success(`Plan changed to ${pkg.name}`);
                                }}
                            >
                                {pkg.isPopular && (
                                    <Box sx={{ 
                                        position: 'absolute', top: 12, right: 12,
                                        bgcolor: palette.amber, color: '#fff',
                                        px: 1.5, py: 0.5, borderRadius: '20px',
                                        fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase'
                                    }}>
                                        Popular
                                    </Box>
                                )}
                                <CardContent sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Avatar sx={{ 
                                        width: 56, height: 56, borderRadius: '14px',
                                        mx: 'auto', mb: 1.5,
                                        background: isSelected ? `linear-gradient(135deg, ${color}, ${color}80)` : palette.hairline,
                                        color: isSelected ? '#fff' : palette.inkSoft
                                    }}>
                                        {index === 0 && <StarIcon />}
                                        {index === 1 && <RocketLaunchIcon />}
                                        {index === 2 && <BusinessIcon />}
                                        {index === 3 && <AdminPanelSettingsIcon />}
                                        {index === 4 && <PackageIcon />}
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700} sx={{ color: palette.ink }}>{pkg.name}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5, my: 1 }}>
                                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '2rem', color: isSelected ? color : palette.ink }}>
                                            {currencySymbol}{pkg.price}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                            /{pkg.billingCycle === 'monthly' ? 'mo' : pkg.billingCycle === 'annual' ? 'yr' : pkg.billingCycle === 'quarterly' ? 'qtr' : 'once'}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ color: palette.inkSoft, display: 'block', mb: 2, minHeight: 32 }}>
                                        {pkg.description || 'Standard features included'}
                                    </Typography>
                                    <Button 
                                        variant={isSelected ? 'contained' : 'outlined'} 
                                        fullWidth 
                                        sx={{ 
                                            mt: 'auto', borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                                            py: 1.2,
                                            background: isSelected ? `linear-gradient(140deg, ${color}, ${color}80)` : 'transparent',
                                            borderColor: isSelected ? color : color,
                                            color: isSelected ? '#fff' : color,
                                            '&:hover': { 
                                                background: isSelected ? `linear-gradient(140deg, ${color}, ${color}80)` : `${rgba(color, 0.08)}`
                                            }
                                        }}
                                    >
                                        {isSelected ? 'Current Plan' : 'Select Plan'}
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}

            <GlassPanel accent={palette.pine} hover={false}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ color: palette.ink }}>Auto-Renew</Typography>
                        <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                            Your subscription will automatically renew at the end of each billing cycle
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={settings.autoRenew !== false}
                                onChange={() => setSettings({ ...settings, autoRenew: !settings.autoRenew })}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                                }}
                            />
                        }
                        label={settings.autoRenew !== false ? 'Enabled' : 'Disabled'}
                        sx={{ m: 0 }}
                    />
                </Box>
            </GlassPanel>

            <Box sx={{ mt: 4 }}>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                    fullWidth
                    sx={{
                        borderRadius: '14px', textTransform: 'none', fontWeight: 700, py: 1.5,
                        background: `linear-gradient(140deg, ${palette.plum}, ${palette.plumGlow})`,
                        boxShadow: `0 12px 30px -8px ${rgba(palette.plum, 0.7)}`,
                        '&:hover': { boxShadow: `0 16px 40px -8px ${rgba(palette.plum, 0.8)}` },
                    }}
                >
                    {saving ? 'Saving Subscription Settings...' : 'Save Subscription Settings'}
                </Button>
            </Box>
        </Box>
    );
};

// ── PACKAGES COMPONENT ──
const PackagesComponent = ({ 
    packages, openPackageDialog, setOpenPackageDialog, packageForm, setPackageForm,
    editingPackage, handleSavePackage, handleDeletePackage, handleTogglePackageStatus,
    handleOpenEditPackage, featureInput, setFeatureInput, handleAddFeature,
    handleRemoveFeature, customSettingKey, setCustomSettingKey, customSettingValue,
    setCustomSettingValue, handleAddCustomSetting, handleRemoveCustomSetting, resetPackageForm
}) => {
    const getColor = (index) => {
        const colors = [palette.rust, palette.pine, palette.teal, palette.gold, palette.plum];
        return colors[index % colors.length];
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <PanelHead icon={<PackageIcon />} title="Meeting Packages" chip={`${packages.length} Packages`} accent={palette.rust} dense />
                <Button 
                    variant="contained" 
                    disableElevation 
                    startIcon={<AddIcon />} 
                    onClick={() => { resetPackageForm(); setOpenPackageDialog(true); }}
                    sx={{
                        borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                        background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                        boxShadow: `0 8px 20px -6px ${rgba(palette.rust, 0.5)}`,
                        '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.rust, 0.6)}` },
                    }}
                >
                    Create Package
                </Button>
            </Box>
            <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />

            {packages.length === 0 ? (
                <GlassPanel accent={palette.rust} hover={false} sx={{ textAlign: 'center', py: 4 }}>
                    <PackageIcon sx={{ fontSize: 56, color: palette.inkSoft, opacity: 0.3, mb: 2 }} />
                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                        No Packages Created
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 0.5, mb: 3 }}>
                        Create your first meeting package to get started
                    </Typography>
                    <Button 
                        variant="contained" 
                        disableElevation 
                        startIcon={<AddIcon />} 
                        onClick={() => { resetPackageForm(); setOpenPackageDialog(true); }}
                        sx={{
                            borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                            background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                            boxShadow: `0 8px 20px -6px ${rgba(palette.rust, 0.5)}`,
                            '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.rust, 0.6)}` },
                        }}
                    >
                        Create First Package
                    </Button>
                </GlassPanel>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 3 }}>
                    {packages.map((pkg, index) => {
                        const color = getColor(index);
                        return (
                            <AnimatedSection key={pkg.id} delay={index * 0.05}>
                                <Card sx={{ 
                                    borderRadius: '16px', 
                                    border: `1px solid ${pkg.isActive ? rgba(color, 0.3) : palette.hairline}`,
                                    boxShadow: pkg.isActive ? `0 4px 20px ${rgba(color, 0.15)}` : '0 1px 3px rgba(33,26,20,0.04)',
                                    transition: 'all 0.3s ease',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    bgcolor: 'rgba(255,255,255,0.6)',
                                    backdropFilter: 'blur(10px)',
                                    '&:hover': { 
                                        boxShadow: pkg.isActive ? `0 8px 40px ${rgba(color, 0.25)}` : '0 8px 24px rgba(33,26,20,0.06)',
                                        borderColor: rgba(color, 0.5)
                                    }
                                }}>
                                    <CardContent sx={{ p: 3, flex: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                                                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                                                        {pkg.name}
                                                    </Typography>
                                                    {pkg.isPopular && (
                                                        <Chip label="⭐ Popular" size="small" sx={{ bgcolor: palette.amber, color: '#fff', fontWeight: 700, fontSize: '0.6rem', height: 22 }} />
                                                    )}
                                                    <Chip 
                                                        label={pkg.isActive ? 'Active' : 'Inactive'} 
                                                        size="small" 
                                                        sx={{ 
                                                            bgcolor: pkg.isActive ? `${palette.pine}15` : `${palette.inkSoft}10`,
                                                            color: pkg.isActive ? palette.pine : palette.inkSoft,
                                                            fontWeight: 600, fontSize: '0.6rem', height: 22 
                                                        }} 
                                                    />
                                                </Box>
                                                <Typography variant="body2" sx={{ color: palette.inkSoft, mb: 1.5 }}>
                                                    {pkg.description || 'No description provided'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                    <Chip icon={<ScheduleIcon sx={{ fontSize: 14 }} />} label={`${pkg.duration} min`} size="small" sx={{ bgcolor: `${color}12`, color, fontWeight: 600 }} />
                                                    <Chip icon={<GroupIcon sx={{ fontSize: 14 }} />} label={`${pkg.maxParticipants} participants`} size="small" sx={{ bgcolor: `${color}12`, color, fontWeight: 600 }} />
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.5rem', color: palette.ink }}>
                                                    ${pkg.price || 0}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: palette.inkSoft, textTransform: 'capitalize' }}>
                                                    {pkg.billingCycle || 'one-time'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleOpenEditPackage(pkg)} sx={{ color: palette.amber, '&:hover': { bgcolor: `${palette.amber}12` } }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleDeletePackage(pkg.id)} sx={{ color: palette.inkSoft, '&:hover': { bgcolor: `${palette.inkSoft}12` } }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={pkg.isActive ? 'Deactivate' : 'Activate'}>
                                                        <IconButton size="small" onClick={() => handleTogglePackageStatus(pkg.id)} sx={{ color: pkg.isActive ? palette.pine : palette.inkSoft, '&:hover': { bgcolor: `${pkg.isActive ? palette.pine : palette.inkSoft}12` } }}>
                                                            {pkg.isActive ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </AnimatedSection>
                        );
                    })}
                </Box>
            )}

            {/* Package Dialog */}
            <Dialog open={openPackageDialog} onClose={() => { setOpenPackageDialog(false); resetPackageForm(); }} maxWidth="md" fullWidth>
                <GlassPanel accent={palette.rust} padding={{ xs: 2.5, md: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                            {editingPackage ? <EditIcon sx={{ color: palette.amber, mr: 1 }} /> : <PackageIcon sx={{ color: palette.rust, mr: 1 }} />}
                            {editingPackage ? 'Edit Package' : 'Create New Package'}
                        </Typography>
                        <IconButton onClick={() => { setOpenPackageDialog(false); resetPackageForm(); }} sx={{ color: palette.inkSoft }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <GlassInput
                                label="Package Name"
                                value={packageForm.name}
                                onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                                required
                                icon={<PackageIcon sx={{ color: palette.rust }} />}
                                placeholder="e.g., Pro Meeting Plan"
                                color={palette.rust}
                            />
                        </Box>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <GlassInput
                                label="Description"
                                value={packageForm.description}
                                onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                                multiline
                                rows={2}
                                icon={<BusinessIcon sx={{ color: palette.teal }} />}
                                placeholder="Brief description of this package"
                                color={palette.rust}
                            />
                        </Box>
                        <GlassInput
                            label="Duration (minutes)"
                            type="number"
                            value={packageForm.duration}
                            onChange={(e) => setPackageForm({ ...packageForm, duration: parseInt(e.target.value) || 0 })}
                            icon={<ScheduleIcon sx={{ color: palette.amber }} />}
                            color={palette.rust}
                        />
                        <GlassInput
                            label="Max Participants"
                            type="number"
                            value={packageForm.maxParticipants}
                            onChange={(e) => setPackageForm({ ...packageForm, maxParticipants: parseInt(e.target.value) || 0 })}
                            icon={<GroupIcon sx={{ color: palette.teal }} />}
                            color={palette.rust}
                        />
                        <GlassInput
                            label="Price ($)"
                            type="number"
                            value={packageForm.price}
                            onChange={(e) => setPackageForm({ ...packageForm, price: parseFloat(e.target.value) || 0 })}
                            icon={<PaymentIcon sx={{ color: palette.pine }} />}
                            color={palette.rust}
                        />
                        <GlassSelect
                            label="Billing Cycle"
                            value={packageForm.billingCycle || 'one-time'}
                            onChange={(e) => setPackageForm({ ...packageForm, billingCycle: e.target.value })}
                            options={[
                                { value: 'one-time', label: 'One Time' },
                                { value: 'monthly', label: 'Monthly' },
                                { value: 'quarterly', label: 'Quarterly' },
                                { value: 'annual', label: 'Annual' },
                            ]}
                            color={palette.rust}
                        />
                        <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', pt: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={packageForm.isActive !== false}
                                        onChange={(e) => setPackageForm({ ...packageForm, isActive: e.target.checked })}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                                        }}
                                    />
                                }
                                label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Active</Typography>}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={packageForm.isPopular || false}
                                        onChange={(e) => setPackageForm({ ...packageForm, isPopular: e.target.checked })}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: palette.amber },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.amber },
                                        }}
                                    />
                                }
                                label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Popular</Typography>}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, mt: 3, justifyContent: 'flex-end' }}>
                        <Button onClick={() => { setOpenPackageDialog(false); resetPackageForm(); }} sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={handleSavePackage}
                            startIcon={editingPackage ? <SaveIcon /> : <AddIcon />}
                            sx={{
                                borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3, py: 1.1,
                                background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                boxShadow: `0 8px 20px -6px ${rgba(palette.rust, 0.5)}`,
                                '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.rust, 0.6)}` },
                            }}
                        >
                            {editingPackage ? 'Update Package' : 'Create Package'}
                        </Button>
                    </Box>
                </GlassPanel>
            </Dialog>
        </Box>
    );
};

// ── SUPER ADMIN MANAGEMENT ──
const SuperAdminManagementComponent = ({ 
    superAdmins, currentUserId, openAddAdmin, setOpenAddAdmin, openEditAdmin, setOpenEditAdmin,
    newSuperAdmin, setNewSuperAdmin, editingAdmin, editAdminData, setEditAdminData,
    handleAddSuperAdmin, handleEditSuperAdmin, handleDeleteSuperAdmin, handleOpenEditDialog,
    handleManualVerifyAdmin, handleResendAdminVerification, showDialogPassword, setShowDialogPassword
}) => (
    <Box>
        <PanelHead icon={<AdminIcon />} title="Super Admin Management" chip={`${superAdmins.length} Admins`} accent={palette.inkSoft} />
        <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                onClick={() => setOpenAddAdmin(true)}
                sx={{
                    borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                    background: `linear-gradient(140deg, ${palette.inkSoft}, #8f3a12)`,
                    boxShadow: `0 8px 20px -6px ${rgba(palette.inkSoft, 0.5)}`,
                    '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.inkSoft, 0.6)}` },
                }}
            >
                Add Super Admin
            </Button>
        </Box>

        <GlassPanel accent={palette.inkSoft} hover={false} padding={{ xs: 0, md: 0 }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, color: palette.inkSoft }}>Admin</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: palette.inkSoft }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: palette.inkSoft }}>Role</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: palette.inkSoft }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: palette.inkSoft }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {superAdmins.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                    <Typography variant="body2" sx={{ color: palette.inkSoft }}>No super admins found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            superAdmins.map((admin, index) => (
                                <motion.tr 
                                    key={admin.id} 
                                    initial={{ opacity: 0, y: 10 }} 
                                    animate={{ opacity: 1, y: 0 }} 
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                >
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Avatar sx={{ 
                                                width: 36, height: 36, 
                                                bgcolor: admin.id === currentUserId ? palette.rust : palette.inkSoft,
                                                fontSize: '0.875rem', fontWeight: 600, borderRadius: '10px',
                                                boxShadow: `0 4px 12px ${rgba(palette.inkSoft, 0.3)}`
                                            }}>
                                                {admin.displayName?.charAt(0) || 'A'}
                                            </Avatar>
                                            <Typography sx={{ fontWeight: 600, color: palette.ink }}>
                                                {admin.displayName}
                                                {admin.id === currentUserId && (
                                                    <Chip label="You" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem', bgcolor: `${palette.rust}15`, color: palette.rust, fontWeight: 700 }} />
                                                )}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ color: palette.inkSoft }}>{admin.email}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={admin.role === 'super_admin' ? 'Super Admin' : 'Admin'} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: admin.role === 'super_admin' ? `${palette.inkSoft}15` : `${palette.teal}15`,
                                                color: admin.role === 'super_admin' ? palette.inkSoft : palette.teal,
                                                fontWeight: 600 
                                            }} 
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: .7, flexWrap: 'wrap' }}>
                                            <Chip 
                                                label={admin.isActive ? 'Active' : 'Inactive'} 
                                                size="small" 
                                                sx={{ 
                                                    bgcolor: admin.isActive ? `${palette.pine}15` : `${palette.inkSoft}10`,
                                                    color: admin.isActive ? palette.pine : palette.inkSoft,
                                                    fontWeight: 600 
                                                }} 
                                            />
                                            <Chip
                                                label={(admin.isVerified || admin.emailVerified) ? 'Email verified' : 'Email unverified'}
                                                size="small"
                                                sx={{ bgcolor: (admin.isVerified || admin.emailVerified) ? `${palette.pine}12` : `${palette.amber}15`, color: (admin.isVerified || admin.emailVerified) ? palette.pine : palette.amber, fontWeight: 700 }}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box display="flex" justifyContent="flex-end" gap={0.5}>
                                            {!(admin.isVerified || admin.emailVerified) && (
                                                <>
                                                    <Tooltip title="Verify Email Manually">
                                                        <IconButton onClick={() => handleManualVerifyAdmin(admin)} size="small" sx={{ color: palette.pine, '&:hover': { bgcolor: `${palette.pine}12` } }}>
                                                            <CheckCircleIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Resend Verification Email">
                                                        <IconButton onClick={() => handleResendAdminVerification(admin)} size="small" sx={{ color: palette.teal, '&:hover': { bgcolor: `${palette.teal}12` } }}>
                                                            <EmailIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            <Tooltip title="Edit Admin">
                                                <IconButton onClick={() => handleOpenEditDialog(admin)} size="small" sx={{ color: palette.amber, '&:hover': { bgcolor: `${palette.amber}12` } }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {admin.id !== currentUserId && (
                                                <Tooltip title="Remove Admin">
                                                    <IconButton onClick={() => handleDeleteSuperAdmin(admin.id)} size="small" sx={{ color: palette.inkSoft, '&:hover': { bgcolor: `${palette.inkSoft}12` } }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </motion.tr>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </GlassPanel>

        {/* Add Admin Dialog */}
        <Dialog open={openAddAdmin} onClose={() => setOpenAddAdmin(false)} maxWidth="sm" fullWidth>
            <GlassPanel accent={palette.inkSoft} padding={{ xs: 2.5, md: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                        <AdminIcon sx={{ color: palette.inkSoft, mr: 1 }} /> Add Super Admin
                    </Typography>
                    <IconButton onClick={() => setOpenAddAdmin(false)} sx={{ color: palette.inkSoft }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />

                <Box sx={{ display: 'grid', gap: 2 }}>
                    <GlassInput
                        label="Email Address"
                        type="email"
                        value={newSuperAdmin.email}
                        onChange={(e) => setNewSuperAdmin({ ...newSuperAdmin, email: e.target.value })}
                        required
                        icon={<EmailIcon sx={{ color: palette.rust }} />}
                        color={palette.inkSoft}
                    />
                    <GlassInput
                        label="Display Name"
                        value={newSuperAdmin.displayName}
                        onChange={(e) => setNewSuperAdmin({ ...newSuperAdmin, displayName: e.target.value })}
                        icon={<PeopleAltIcon sx={{ color: palette.rust }} />}
                        color={palette.inkSoft}
                    />
                    <GlassInput
                        label="Password"
                        type={showDialogPassword ? 'text' : 'password'}
                        value={newSuperAdmin.password}
                        onChange={(e) => setNewSuperAdmin({ ...newSuperAdmin, password: e.target.value })}
                        required
                        icon={<LockIcon sx={{ color: palette.rust }} />}
                        color={palette.inkSoft}
                        slotProps={{ input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowDialogPassword(!showDialogPassword)} edge="end">
                                        {showDialogPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        } }}
                    />
                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                        <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                            Authentication stores the account securely; the verification email is sent from your configured professional company SMTP mailbox.
                        </Typography>
                    </Alert>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, mt: 3, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setOpenAddAdmin(false)} sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleAddSuperAdmin}
                        startIcon={<AddIcon />}
                        sx={{
                            borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3, py: 1.1,
                            background: `linear-gradient(140deg, ${palette.inkSoft}, #8f3a12)`,
                            boxShadow: `0 8px 20px -6px ${rgba(palette.inkSoft, 0.5)}`,
                            '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.inkSoft, 0.6)}` },
                        }}
                    >
                        Add Super Admin
                    </Button>
                </Box>
            </GlassPanel>
        </Dialog>

        {/* Edit Admin Dialog */}
        <Dialog open={openEditAdmin} onClose={() => setOpenEditAdmin(false)} maxWidth="sm" fullWidth>
            <GlassPanel accent={palette.amber} padding={{ xs: 2.5, md: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink }}>
                        <EditIcon sx={{ color: palette.amber, mr: 1 }} /> Edit Super Admin
                    </Typography>
                    <IconButton onClick={() => setOpenEditAdmin(false)} sx={{ color: palette.inkSoft }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />

                <Box sx={{ display: 'grid', gap: 2 }}>
                    <GlassInput
                        label="Display Name"
                        value={editAdminData.displayName || ''}
                        onChange={(e) => setEditAdminData({ ...editAdminData, displayName: e.target.value })}
                        icon={<PeopleAltIcon sx={{ color: palette.rust }} />}
                        color={palette.amber}
                    />
                    <GlassSelect
                        label="Role"
                        value={editAdminData.role || 'super_admin'}
                        onChange={(e) => setEditAdminData({ ...editAdminData, role: e.target.value })}
                        options={[
                            { value: 'super_admin', label: 'Super Admin' },
                            { value: 'admin', label: 'Admin' },
                            { value: 'moderator', label: 'Moderator' },
                        ]}
                        color={palette.amber}
                    />
                    <GlassInput
                        label="New Password (optional)"
                        type="password"
                        value={editAdminData.password || ''}
                        onChange={(e) => setEditAdminData({ ...editAdminData, password: e.target.value })}
                        icon={<LockIcon sx={{ color: palette.rust }} />}
                        helperText="Leave blank to keep current password"
                        color={palette.amber}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={editAdminData.isActive !== false}
                                onChange={(e) => setEditAdminData({ ...editAdminData, isActive: e.target.checked })}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: palette.pine },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: palette.pine },
                                }}
                            />
                        }
                        label="Active"
                    />
                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                        <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                            If you enter a new password, a password reset email will be sent to the admin
                        </Typography>
                    </Alert>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, mt: 3, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setOpenEditAdmin(false)} sx={{ color: palette.inkSoft, fontWeight: 600, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleEditSuperAdmin}
                        startIcon={<SaveIcon />}
                        sx={{
                            borderRadius: '12px', textTransform: 'none', fontWeight: 700, px: 3, py: 1.1,
                            background: `linear-gradient(140deg, ${palette.amber}, ${palette.amberGlow})`,
                            boxShadow: `0 8px 20px -6px ${rgba(palette.amber, 0.5)}`,
                            '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.amber, 0.6)}` },
                        }}
                    >
                        Save Changes
                    </Button>
                </Box>
            </GlassPanel>
        </Dialog>
    </Box>
);

// ── DATA MANAGEMENT ──
// ── DATA MANAGEMENT ──
const DataManagementComponent = ({
    selectedCollections, setSelectedCollections,
    isBackingUp, handleDownloadBackup,
    restoreFile, setRestoreFile,
    isRestoring, handleRestoreBackup,
    isWiping, handleWipeData
}) => {
    const [openWipeDialog, setOpenWipeDialog] = useState(false);
    const [wipeConfirmText, setWipeConfirmText] = useState('');

    const FIRESTORE_COLLECTIONS = [
        { id: 'users', desc: 'User accounts and profiles' },
        { id: 'companies', desc: 'Company profiles and services' },
        { id: 'meetings', desc: 'Scheduled meetings' },
        { id: 'meetingRequests', desc: 'Meeting requests pending approval' },
        { id: 'matchmakingScores', desc: 'AI compatibility scores' },
        { id: 'analytics', desc: 'Platform analytics' },
        { id: 'notifications', desc: 'User notifications' },
        { id: 'virtualRooms', desc: 'Video meeting rooms' },
        { id: 'sponsorships', desc: 'Sponsorship packages' },
        { id: 'chatMessages', desc: 'Chat messages' },
        { id: 'chatRooms', desc: 'Chat rooms' },
        { id: 'deals', desc: 'Business deals' },
        { id: 'auditLogs', desc: 'Audit logs' },
        { id: 'reports', desc: 'Generated reports' },
        { id: 'systemConfig', desc: 'System configuration' },
        { id: 'invitations', desc: 'Team invitations' },
        { id: 'otps', desc: 'OTP verification codes' },
        { id: 'rateLimits', desc: 'Rate limiting for login attempts' }
    ];

    const handleSelectAll = () => setSelectedCollections(FIRESTORE_COLLECTIONS.map(col => col.id));
    const handleDeselectAll = () => setSelectedCollections([]);
    const handleCollectionToggle = (colId) => {
        if (selectedCollections.includes(colId)) {
            setSelectedCollections(selectedCollections.filter(id => id !== colId));
        } else {
            setSelectedCollections([...selectedCollections, colId]);
        }
    };

    const executeWipe = () => {
        if (wipeConfirmText === 'DELETE') {
            handleWipeData();
            setOpenWipeDialog(false);
            setWipeConfirmText('');
        } else {
            toast.error('Type DELETE to confirm');
        }
    };

    return (
        <Box>
            
<PanelHead icon={<SettingsBackupRestoreIcon />} title="Data Management" chip="Backup, Restore &amp; Wipe" accent={palette.teal} />
            <Divider sx={{ mb: 3, borderColor: palette.hairlineSoft }} />

            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
                {/* Backup Section */}
                <GlassPanel accent={palette.teal} delay={0.1} padding={{ xs: 2, md: 2.5 }}>
                    <PanelHead icon={<CloudDownloadIcon />} title="Export Backup" subtitle="Select collections to backup" accent={palette.teal} dense />
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button size="small" variant="outlined" onClick={handleSelectAll} sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem' }}>
                            Select All
                        </Button>
                        <Button size="small" variant="outlined" onClick={handleDeselectAll} sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.75rem' }}>
                            Deselect All
                        </Button>
                    </Box>

                    <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                        {FIRESTORE_COLLECTIONS.map((col) => (
                            <FormControlLabel
                                key={col.id}
                                control={
                                    <Checkbox
                                        checked={selectedCollections.includes(col.id)}
                                        onChange={() => handleCollectionToggle(col.id)}
                                        sx={{ color: palette.teal, '&.Mui-checked': { color: palette.teal } }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: palette.ink }}>{col.id}</Typography>
                                        <Typography variant="caption" sx={{ color: palette.inkLight }}>{col.desc}</Typography>
                                    </Box>
                                }
                                sx={{ width: '100%', m: 0, py: 0.5 }}
                            />
                        ))}
                    </Box>

                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={isBackingUp ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
                        onClick={handleDownloadBackup}
                        disabled={isBackingUp || selectedCollections.length === 0}
                        fullWidth
                        sx={{
                            borderRadius: '12px', textTransform: 'none', fontWeight: 600, py: 1.2,
                            background: `linear-gradient(140deg, ${palette.teal}, ${palette.tealGlow})`,
                            boxShadow: `0 8px 20px -6px ${rgba(palette.teal, 0.5)}`,
                            '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.teal, 0.6)}` },
                        }}
                    >
                        {isBackingUp ? 'Preparing Backup...' : `Download Backup (${selectedCollections.length})`}
                    </Button>
                </GlassPanel>

                {/* Restore Section */}
                <GlassPanel accent={palette.amber} delay={0.15} padding={{ xs: 2, md: 2.5 }}>
                    <PanelHead icon={<CloudUploadIcon />} title="Restore Backup" subtitle="Upload a backup file" accent={palette.amber} dense />
                    
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Data Overwrite Warning</Typography>
                        <Typography variant="caption">Restoring will overwrite existing documents with matching IDs.</Typography>
                    </Alert>

                    <Box
                        sx={{
                            border: `2px dashed ${restoreFile ? palette.success : palette.hairlineStrong}`,
                            borderRadius: '12px', p: 3, textAlign: 'center',
                            bgcolor: restoreFile ? 'rgba(28,138,86,0.05)' : 'rgba(255,255,255,0.3)',
                            cursor: 'pointer', transition: 'all 0.3s ease',
                            '&:hover': { borderColor: palette.amber },
                        }}
                        onClick={() => document.getElementById('restore-file-upload').click()}
                    >
                        <input
                            type="file"
                            accept=".json"
                            id="restore-file-upload"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                    setRestoreFile(e.target.files[0]);
                                }
                            }}
                        />
                        <StorageIcon sx={{ fontSize: 40, color: restoreFile ? palette.success : palette.inkLight, mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: restoreFile ? palette.success : palette.ink }}>
                            {restoreFile ? restoreFile.name : 'Click to Browse JSON Backup'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: palette.inkLight }}>
                            {restoreFile ? 'Ready to process.' : 'Only valid platform backups allowed.'}
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={isRestoring ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                        onClick={handleRestoreBackup}
                        disabled={isRestoring || !restoreFile}
                        fullWidth
                        sx={{
                            borderRadius: '12px', textTransform: 'none', fontWeight: 600, py: 1.2, mt: 2,
                            background: `linear-gradient(140deg, ${palette.amber}, ${palette.amberGlow})`,
                            boxShadow: `0 8px 20px -6px ${rgba(palette.amber, 0.5)}`,
                            '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.amber, 0.6)}` },
                        }}
                    >
                        {isRestoring ? 'Restoring Data...' : 'Restore Now'}
                    </Button>
                </GlassPanel>

                {/* Wipe Section */}
                <GlassPanel accent={palette.danger} delay={0.2} padding={{ xs: 2, md: 2.5 }} sx={{ gridColumn: { lg: '1 / -1' } }}>
                    <PanelHead icon={<DeleteForeverIcon />} title="Danger Zone" subtitle="Permanently delete data" accent={palette.danger} dense />
                    
                    <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: palette.danger }}>Irreversible Action</Typography>
                        <Typography variant="caption">Wiping data cannot be undone. Please ensure you have downloaded a backup first.</Typography>
                    </Alert>

                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<DeleteForeverIcon />}
                        onClick={() => {
                            if (selectedCollections.length === 0) {
                                toast.error('Select at least one collection first.');
                                return;
                            }
                            setOpenWipeDialog(true);
                        }}
                        fullWidth
                        sx={{
                            borderRadius: '12px', textTransform: 'none', fontWeight: 600, py: 1.2,
                            background: `linear-gradient(140deg, ${palette.danger}, #b91c1c)`,
                            boxShadow: `0 8px 20px -6px ${rgba(palette.danger, 0.5)}`,
                            '&:hover': { boxShadow: `0 12px 28px -6px ${rgba(palette.danger, 0.6)}` },
                        }}
                    >
                        Wipe Selected Collections ({selectedCollections.length})
                    </Button>
                </GlassPanel>
            </Box>

            {/* Wipe Confirmation Dialog */}
            <Dialog open={openWipeDialog} onClose={() => setOpenWipeDialog(false)} maxWidth="sm" fullWidth>
                <GlassPanel accent={palette.danger} padding={{ xs: 2.5, md: 3 }}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <GlassIcon icon={<WarningIcon />} color={palette.danger} size={56} />
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 700, fontSize: '1.2rem', color: palette.ink, mt: 1 }}>
                            Confirm Permanent Deletion
                        </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ color: palette.inkSoft, mb: 2 }}>
                        You are about to permanently wipe all documents in the following collections:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                        {selectedCollections.map(col => (
                            <Chip key={col} label={col} size="small" sx={{ bgcolor: 'rgba(201,52,43,0.1)', color: palette.danger }} />
                        ))}
                    </Box>

                    <Typography variant="body2" sx={{ color: palette.inkSoft, mb: 2 }}>
                        Type <strong>DELETE</strong> to confirm:
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        value={wipeConfirmText}
                        onChange={(e) => setWipeConfirmText(e.target.value)}
                        placeholder="DELETE"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                borderColor: wipeConfirmText === 'DELETE' ? palette.success : palette.danger,
                                '&.Mui-focused': {
                                    borderColor: wipeConfirmText === 'DELETE' ? palette.success : palette.danger,
                                },
                            },
                        }}
                    />

                    <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                        <Button
                            fullWidth
                            onClick={() => setOpenWipeDialog(false)}
                            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, color: palette.inkSoft }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            onClick={executeWipe}
                            disabled={wipeConfirmText !== 'DELETE' || isWiping}
                            startIcon={isWiping ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
                            sx={{
                                borderRadius: '12px', textTransform: 'none', fontWeight: 600, py: 1.2,
                                background: `linear-gradient(140deg, ${palette.danger}, #b91c1c)`,
                                '&:disabled': { opacity: 0.5 },
                            }}
                        >
                            {isWiping ? 'Wiping...' : 'Permanently Delete'}
                        </Button>
                    </Box>
                </GlassPanel>
            </Dialog>
        </Box>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   9 · MAIN SETTINGS COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const Settings = () => {
    const { user } = useAuth();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const tabForPath = (pathname = '') => {
        if (pathname.includes('/settings/security')) return 1;
        if (/\/settings\/(email|payment|notifications|integrations)/.test(pathname)) return 2;
        if (pathname.includes('/settings/backup')) return 6;
        return 0;
    };
    const [tabValue, setTabValue] = useState(() => tabForPath(location.pathname));
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [openAddAdmin, setOpenAddAdmin] = useState(false);
    const [openEditAdmin, setOpenEditAdmin] = useState(false);
    const [superAdmins, setSuperAdmins] = useState([]);
    const [newSuperAdmin, setNewSuperAdmin] = useState({ email: '', displayName: '', password: '', role: 'super_admin' });
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [editAdminData, setEditAdminData] = useState({ displayName: '', role: '', isActive: true });
    const [testingConnection, setTestingConnection] = useState(false);
    const [packages, setPackages] = useState([]);
    const [openPackageDialog, setOpenPackageDialog] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const [packageForm, setPackageForm] = useState({
        name: '', description: '', duration: 30, maxParticipants: 10, price: 0,
        billingCycle: 'one-time', allowRecording: true, allowCustomBranding: false,
        allowScreenShare: true, allowChat: true, isActive: true, isPopular: false,
        features: [], customSettings: {}
    });
    const [featureInput, setFeatureInput] = useState('');
    const [customSettingKey, setCustomSettingKey] = useState('');
    const [customSettingValue, setCustomSettingValue] = useState('');
    const [showDialogPassword, setShowDialogPassword] = useState(false);

    const [selectedBackupCollections, setSelectedBackupCollections] = useState([]);
    const [isBackingUp, setIsBackingUp] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [isWiping, setIsWiping] = useState(false);
    const [restoreFile, setRestoreFile] = useState(null);

    const [gatewayConfigs, setGatewayConfigs] = useState([]);
    const [openGatewayDialog, setOpenGatewayDialog] = useState(false);
    const [editingGateway, setEditingGateway] = useState(null);
    const [selectedGateway, setSelectedGateway] = useState(null);
    const [gatewayForm, setGatewayForm] = useState({
        provider: '', currency: 'EUR', mode: 'live', status: 'active',
        publicKey: '', description: '',
        bankDetails: { accountName: '', accountNumber: '', bankName: '', routingNumber: '', iban: '', swift: '' },
        enableWebhooks: true, enableRefunds: true, enableRecurring: false,
        minimumAmount: 1, maximumAmount: 100000, fee: 0,
    });

    const [generalSettings, setGeneralSettings] = useState({
        platformName: 'TELCOFAIR', logo: '', currency: 'EUR', favicon: '',
        maintenanceMode: false, defaultLanguage: 'en', timezone: 'UTC', dateFormat: 'MM/DD/YYYY',
        primaryColor: '#2E6F8E', secondaryColor: '#42B89B', accentColor: '#7C5CF2'
    });

    const [securitySettings, setSecuritySettings] = useState({
        twoFactorRequired: false, sessionTimeout: 60, maxLoginAttempts: 5, lockoutDuration: 30,
        passwordMinLength: 8, requireUppercase: true, requireLowercase: true, requireNumbers: true, requireSpecialChars: true
    });

    const [emailSettings, setEmailSettings] = useState({
        smtpHost: '', smtpPort: 587, smtpSecure: true, smtpUser: '', smtpPass: '', fromEmail: '', fromName: ''
    });

    const [paymentSettings, setPaymentSettings] = useState({
        provider: 'revolut', taxRate: 0, invoicePrefix: 'INV-', enableRecurring: true, enableRefunds: true, minimumAmount: 1
    });

    const [subscriptionSettings, setSubscriptionSettings] = useState({
        plan: 'pro', currency: 'EUR', status: 'active', nextBillingDate: '2024-04-15', autoRenew: true,
    });

    const [smsSettings, setSmsSettings] = useState({
        provider: 'twilio', defaultCountryCode: '+1', enableDeliveryReports: true, maxRetries: 3, retryDelay: 60,
    });

    const [whatsappSettings, setWhatsappSettings] = useState({
        provider: 'twilio', enableTemplates: true, enableDeliveryReports: true, maxRetries: 3, retryDelay: 60,
    });

    useEffect(() => {
        setTabValue(tabForPath(location.pathname));
    }, [location.pathname]);

    // ── FEATURE MANAGEMENT ──
    const handleAddFeature = () => {
        if (featureInput.trim()) {
            setPackageForm({ 
                ...packageForm, 
                features: [...packageForm.features, featureInput.trim()] 
            });
            setFeatureInput('');
        }
    };

    const handleRemoveFeature = (index) => {
        setPackageForm({ 
            ...packageForm, 
            features: packageForm.features.filter((_, i) => i !== index) 
        });
    };

    // ── CUSTOM SETTINGS MANAGEMENT ──
    const handleAddCustomSetting = () => {
        if (customSettingKey.trim() && customSettingValue.trim()) {
            setPackageForm({
                ...packageForm,
                customSettings: { 
                    ...packageForm.customSettings, 
                    [customSettingKey.trim()]: customSettingValue.trim() 
                }
            });
            setCustomSettingKey('');
            setCustomSettingValue('');
        }
    };

    const handleRemoveCustomSetting = (key) => {
        const newSettings = { ...packageForm.customSettings };
        delete newSettings[key];
        setPackageForm({ ...packageForm, customSettings: newSettings });
    };

    // ── FETCH ALL SETTINGS ──
    useEffect(() => {
        const loadAllSettings = async () => {
            setLoading(true);
            try {
                const [settings, runtime] = await Promise.all([
                    withTimeout(settingsService.getSettings(), 8000, 'Settings request timed out'),
                    withTimeout(settingsService.getPublicRuntime().catch(() => ({})), 5000, 'Runtime settings request timed out').catch(() => ({})),
                ]);
                if (settings.value?.general || runtime?.theme) {
                    const t = runtime?.theme || {};
                    setGeneralSettings(prev => ({
                        ...prev,
                        ...(settings.value?.general || {}),
                        ...(t.primary ? { primaryColor: t.primary } : {}),
                        ...(t.secondary ? { secondaryColor: t.secondary } : {}),
                        ...(t.accent ? { accentColor: t.accent } : {}),
                    }));
                }
                if (settings.value?.security) setSecuritySettings(prev => ({ ...prev, ...settings.value.security }));
                if (settings.value?.subscription) setSubscriptionSettings(prev => ({ ...prev, ...settings.value.subscription }));
                
                // These lists enrich Settings but must never hold the page in
                // a permanent loading state when one collection is unavailable.
                await Promise.allSettled([
                    withTimeout(fetchSuperAdmins(), 5000, 'Admin list request timed out'),
                    withTimeout(fetchPackages(), 5000, 'Packages request timed out'),
                ]);
            } catch (error) {
                console.error('Error loading settings:', error);
                toast.error('Failed to load settings');
            } finally {
                setLoading(false);
            }
        };
        loadAllSettings();
    }, []);

    const fetchSuperAdmins = async () => {
        try {
            const admins = await settingsService.getSuperAdmins();
            setSuperAdmins(admins);
        } catch (error) {
            console.error('Error fetching super admins:', error);
        }
    };

    const fetchPackages = async () => {
        try {
            const pkgs = await settingsService.getPackages('system');
            setPackages(pkgs);
        } catch (error) {
            console.error('Error fetching packages:', error);
        }
    };

    const fetchGateways = async () => {
        try {
            const gateways = await settingsService.getPaymentGateways('system');
            setGatewayConfigs(gateways);
            const active = gateways.find(g => g.status === 'active');
            if (active) setSelectedGateway(active);
            else if (gateways.length > 0) setSelectedGateway(gateways[0]);
        } catch (error) {
            console.error('Error fetching gateways:', error);
        }
    };

    // ── SAVE HANDLERS ──
    const handleSaveGeneral = async () => {
        setSaving(true);
        try {
            let logoUrl = generalSettings.logo;
            if (logoFile) {
                setUploadingLogo(true);
                logoUrl = await settingsService.uploadLogo(logoFile);
            }
            await settingsService.updateGeneralSettings({ ...generalSettings, logo: logoUrl }, user?.uid);
            setGeneralSettings(prev => ({ ...prev, logo: logoUrl }));
            setLogoFile(null);
            setLogoPreview('');
            toast.success('General settings saved successfully!');
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to save settings'));
        } finally {
            setUploadingLogo(false);
            setSaving(false);
        }
    };

    const handleSaveSecurity = async () => {
        setSaving(true);
        try {
            await settingsService.updateSecuritySettings(securitySettings, user?.uid);
            toast.success('Security settings saved successfully!');
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to save security settings'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveEmail = async () => {
        setSaving(true);
        try {
            await settingsService.updateEmailSettings(emailSettings, user?.uid);
            toast.success('Email settings saved successfully!');
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to save email settings'));
        } finally {
            setSaving(false);
        }
    };

    const handleSavePayment = async () => {
        setSaving(true);
        try {
            await settingsService.updatePaymentSettings(paymentSettings, user?.uid);
            toast.success('Payment settings saved successfully!');
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to save payment settings'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSubscription = async () => {
        setSaving(true);
        try {
            await settingsService.updateSubscriptionSettings(subscriptionSettings, user?.uid);
            toast.success('Subscription settings saved successfully!');
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to save subscription settings'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSms = async () => {
        setSaving(true);
        try {
            await settingsService.updateSmsSettings(smsSettings, user?.uid);
            toast.success('SMS settings saved successfully!');
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to save SMS settings'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveWhatsApp = async () => {
        setSaving(true);
        try {
            await settingsService.updateWhatsAppSettings(whatsappSettings, user?.uid);
            toast.success('WhatsApp settings saved successfully!');
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to save WhatsApp settings'));
        } finally {
            setSaving(false);
        }
    };

    // ── ADMIN MANAGEMENT ──
    const handleAddSuperAdmin = async () => {
        try {
            const result = await settingsService.addSuperAdmin(newSuperAdmin);
            toast.success('Super admin added successfully!');
            if (result?.verificationWarning) toast.error(result.verificationWarning, { duration: 6000 });
            setOpenAddAdmin(false);
            setNewSuperAdmin({ email: '', displayName: '', password: '', role: 'super_admin' });
            fetchSuperAdmins();
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to add super admin'));
        }
    };

    const handleEditSuperAdmin = async () => {
        try {
            await settingsService.updateSuperAdmin(editingAdmin.id, editAdminData);
            if (editAdminData.password) {
                await sendProfessionalPasswordResetEmail(editingAdmin.email);
                toast.info('Password reset email sent');
            }
            toast.success('Super admin updated successfully!');
            setOpenEditAdmin(false);
            setEditingAdmin(null);
            fetchSuperAdmins();
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to update super admin'));
        }
    };

    const handleManualVerifyAdmin = async (admin) => {
        if (!admin?.id) return;
        try {
            await manualVerifyUserEmail(admin.id, true);
            toast.success(`${admin.email || 'Admin'} verified manually`);
            fetchSuperAdmins();
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to verify admin email'));
        }
    };

    const handleResendAdminVerification = async (admin) => {
        if (!admin?.id) return;
        try {
            const result = await sendProfessionalVerificationEmail(admin.id);
            if (result?.requiresUserSession) {
                toast('Firebase verification must be resent by that administrator after they sign in to their verification screen.');
            } else if (result?.manualVerificationRequired) {
                toast('Manual verification is selected. Use Verify Manually for this administrator.');
            } else if (result?.success === false) {
                throw new Error(result?.message || 'Verification delivery was not completed.');
            } else {
                toast.success(result?.provider === 'whatsapp' ? `WhatsApp verification requested for ${admin.email || 'admin'}` : `Verification message sent to ${admin.email || 'admin'}`);
            }
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to send verification email'));
        }
    };

    const handleDeleteSuperAdmin = async (adminId) => {
        if (adminId === user?.uid) {
            toast.error('You cannot delete yourself!');
            return;
        }
        if (window.confirm('Are you sure you want to remove this super admin?')) {
            try {
                await settingsService.deleteSuperAdmin(adminId);
                toast.success('Super admin removed successfully!');
                fetchSuperAdmins();
            } catch (error) {
                toast.error(friendlyError(error, 'Failed to remove super admin'));
            }
        }
    };

    const handleOpenEditDialog = (admin) => {
        setEditingAdmin(admin);
        setEditAdminData({
            displayName: admin.displayName || '',
            role: admin.role || 'super_admin',
            isActive: admin.isActive !== undefined ? admin.isActive : true,
            password: ''
        });
        setOpenEditAdmin(true);
    };

    // ── PACKAGE MANAGEMENT ──
    const handleSavePackage = async () => {
        try {
            if (!packageForm.name) {
                toast.error('Package name is required');
                return;
            }
            if (editingPackage) {
                await settingsService.updatePackage(editingPackage.id, packageForm);
                toast.success('Package updated successfully!');
            } else {
                await settingsService.addPackage(packageForm, 'system');
                toast.success('Package created successfully!');
            }
            setOpenPackageDialog(false);
            setEditingPackage(null);
            resetPackageForm();
            fetchPackages();
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to save package'));
        }
    };

    const handleDeletePackage = async (packageId) => {
        if (window.confirm('Are you sure you want to delete this package?')) {
            try {
                await settingsService.deletePackage(packageId);
                toast.success('Package deleted successfully!');
                fetchPackages();
            } catch (error) {
                toast.error(friendlyError(error, 'Failed to delete package'));
            }
        }
    };

    const handleTogglePackageStatus = async (packageId) => {
        try {
            const pkg = packages.find(p => p.id === packageId);
            await settingsService.updatePackage(packageId, { isActive: !pkg.isActive });
            toast.success(`Package ${pkg.isActive ? 'deactivated' : 'activated'} successfully!`);
            fetchPackages();
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to update package status'));
        }
    };

    const resetPackageForm = () => {
        setPackageForm({
            name: '', description: '', duration: 30, maxParticipants: 10, price: 0,
            billingCycle: 'one-time', allowRecording: true, allowCustomBranding: false,
            allowScreenShare: true, allowChat: true, isActive: true, isPopular: false,
            features: [], customSettings: {}
        });
        setFeatureInput('');
        setCustomSettingKey('');
        setCustomSettingValue('');
        setEditingPackage(null);
    };

    const handleOpenEditPackage = (pkg) => {
        setEditingPackage(pkg);
        setPackageForm({
            name: pkg.name || '', description: pkg.description || '', duration: pkg.duration || 30,
            maxParticipants: pkg.maxParticipants || 10, price: pkg.price || 0,
            billingCycle: pkg.billingCycle || 'one-time',
            allowRecording: pkg.allowRecording !== undefined ? pkg.allowRecording : true,
            allowCustomBranding: pkg.allowCustomBranding !== undefined ? pkg.allowCustomBranding : false,
            allowScreenShare: pkg.allowScreenShare !== undefined ? pkg.allowScreenShare : true,
            allowChat: pkg.allowChat !== undefined ? pkg.allowChat : true,
            isActive: pkg.isActive !== undefined ? pkg.isActive : true,
            isPopular: pkg.isPopular || false, features: pkg.features || [], customSettings: pkg.customSettings || {}
        });
        setOpenPackageDialog(true);
    };

    // ── GATEWAY MANAGEMENT ──
    const handleAddGateway = async () => {
        try {
            if (!gatewayForm.provider) {
                toast.error('Please select a gateway provider');
                return;
            }
            const newGateway = await settingsService.addPaymentGateway(gatewayForm, 'system');
            setGatewayConfigs([...gatewayConfigs, newGateway]);
            if (gatewayConfigs.length === 0) setSelectedGateway(newGateway);
            toast.success('Payment gateway added successfully!');
            setOpenGatewayDialog(false);
            setGatewayForm({});
            setEditingGateway(null);
            fetchGateways();
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to add payment gateway'));
        }
    };

    const handleSaveGateway = async (gatewayId) => {
        try {
            await settingsService.updatePaymentGateway(gatewayId, gatewayForm);
            toast.success('Gateway updated successfully!');
            setOpenGatewayDialog(false);
            setEditingGateway(null);
            setGatewayForm({});
            fetchGateways();
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to update gateway'));
        }
    };

    const handleDeleteGateway = async (gatewayId) => {
        if (!window.confirm('Are you sure you want to delete this payment gateway?')) return;
        try {
            await settingsService.deletePaymentGateway(gatewayId);
            toast.success('Gateway deleted successfully!');
            if (selectedGateway?.id === gatewayId) setSelectedGateway(null);
            fetchGateways();
        } catch (error) {
            toast.error(friendlyError(error, 'Failed to delete gateway'));
        }
    };

    const handleEditGateway = (gateway) => {
        setEditingGateway(gateway);
        setGatewayForm({
            provider: gateway.provider || '',
            currency: gateway.currency || generalSettings.currency || 'EUR',
            mode: gateway.mode || 'live',
            status: gateway.status || 'active',
            publicKey: gateway.publicKey || '',
            description: gateway.description || '',
            bankDetails: gateway.bankDetails || { accountName: '', accountNumber: '', bankName: '', routingNumber: '', iban: '', swift: '' },
            enableWebhooks: gateway.enableWebhooks !== false,
            enableRefunds: gateway.enableRefunds !== false,
            enableRecurring: gateway.enableRecurring || false,
            minimumAmount: gateway.minimumAmount || 1,
            maximumAmount: gateway.maximumAmount || 100000,
            fee: gateway.fee || 0,
        });
        setOpenGatewayDialog(true);
    };

    const handleGatewayChange = (gateway) => setSelectedGateway(gateway);

    // ── DATA MANAGEMENT ──
    const handleDownloadBackup = async () => {
        if (selectedBackupCollections.length === 0) {
            toast.error('Please select at least one collection to back up');
            return;
        }
        setIsBackingUp(true);
        try {
            const backupData = {};
            for (const colName of selectedBackupCollections) {
                backupData[colName] = await settingsService.getCollectionData(colName);
            }
            const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(backupData, null, 2));
            const downloadAnchor = document.createElement('a');
            downloadAnchor.setAttribute("href", dataStr);
            downloadAnchor.setAttribute("download", `telcofair_backup_${new Date().toISOString().split('T')[0]}.json`);
            document.body.appendChild(downloadAnchor);
            downloadAnchor.click();
            downloadAnchor.remove();
            toast.success('Database backup completed successfully!');
        } catch (error) {
            console.error('Error generating backup:', error);
            toast.error('Failed to generate backup file');
        } finally {
            setIsBackingUp(false);
        }
    };

    const handleRestoreBackup = async () => {
        if (!restoreFile) return;
        setIsRestoring(true);
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = JSON.parse(e.target.result);
                for (const [colName, docs] of Object.entries(data)) {
                    if (!Array.isArray(docs)) continue;
                    await settingsService.restoreCollection(colName, docs);
                }
                toast.success('Database restore completed successfully!');
                setRestoreFile(null);
            } catch (error) {
                console.error('Error restoring backup:', error);
                toast.error('Restore failed: Invalid JSON or database error');
            } finally {
                setIsRestoring(false);
            }
        };
        reader.onerror = () => {
            toast.error('Failed to read the backup file');
            setIsRestoring(false);
        };
        reader.readAsText(restoreFile);
    };

    const handleWipeData = async () => {
        if (selectedBackupCollections.length === 0) return;
        setIsWiping(true);
        try {
            for (const colName of selectedBackupCollections) {
                await settingsService.wipeCollection(colName);
            }
            toast.success('Selected collections have been permanently wiped.');
            await Promise.all([fetchSuperAdmins(), fetchPackages()]);
        } catch (error) {
            console.error('Error wiping data:', error);
            toast.error('Failed to wipe data');
        } finally {
            setIsWiping(false);
        }
    };

    const testConnection = async (type) => {
        setTestingConnection(true);
        try {
            await settingsService.testProviderConnection(type, type);
            toast.success(`✅ ${type} connection successful!`);
        } catch (error) {
            toast.error(`❌ ${type} connection failed`);
        } finally {
            setTestingConnection(false);
        }
    };

    const handleLogoChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        const allowed = new Set(['image/png','image/jpeg','image/webp']);
        if (!allowed.has(String(file.type || '').toLowerCase())) { toast.error('Choose a PNG, JPG/JPEG or WEBP logo.'); return; }
        if (Number(file.size || 0) > 5 * 1024 * 1024) { toast.error('Logo must be 5 MB or smaller.'); return; }
        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setLogoPreview(e.target.result);
        reader.readAsDataURL(file);
    };

    const tabs = [
        { label: 'General', icon: <SettingsIcon  key="config-3730-0-1" />, color: palette.rust },
        { label: 'Security', icon: <SecurityIcon  key="config-3731-1-1" />, color: palette.pine },
        { label: 'Platform Services', icon: <StorageIcon  key="config-3732-2-1" />, color: palette.teal },
        { label: 'Subscriptions', icon: <SubscriptionIcon  key="config-3733-3-1" />, color: palette.plum },
        { label: 'Packages', icon: <PackageIcon  key="config-3734-4-1" />, color: palette.rust },
        { label: 'Admins', icon: <AdminIcon  key="config-3735-5-1" />, color: palette.inkSoft },
        { label: 'Data Management', icon: <SettingsBackupRestoreIcon  key="config-3736-6-1" />, color: palette.teal },
    ];

    const shell = {
        minHeight: '100vh',
        position: 'relative',
        fontFamily: fontBody,
        color: palette.ink,
        background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            opacity: 0.3,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
            animation: 'tfGrain 8s steps(6) infinite',
        },
    };

    const statsData = [
        { label: 'Settings Areas', value: 7, icon: <SettingsIcon  key="config-3759-0-2" />, accent: palette.rust, hint: 'Platform controls' },
        { label: 'Packages', value: packages.length, icon: <PackageIcon  key="config-3760-1-2" />, accent: palette.pine, hint: 'Company plans' },
        { label: 'Super Admins', value: superAdmins.length, icon: <AdminIcon  key="config-3761-2-2" />, accent: palette.plum, hint: 'Privileged admins' },
        { label: 'Services', value: 'Central', icon: <StorageIcon  key="config-3762-3-2" />, accent: palette.teal, hint: 'Storage · Email · Payments · AI' },
    ];

    if (loading) {
        return (
            <Box sx={shell}>
                <style>{FONT_IMPORT}</style>
                <LiquidBackdrop />
                
                <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, py: 10 }}>
                    <GlassPanel accent={palette.rust}>
                        <Box sx={{ display: 'grid', placeItems: 'center', gap: 2, py: 5 }}>
                            <CircularProgress sx={{ color: palette.rust }} />
                            <Typography sx={{ color: palette.inkLight, fontSize: '0.88rem' }}>
                                Loading settings...
                            </Typography>
                        </Box>
                    </GlassPanel>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={shell}>
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />
            

            <Container maxWidth={false} disableGutters sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, lg: 5 }, py: { xs: 3, md: 4 } }}>
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3.5 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: fontMono, fontSize: '0.64rem', fontWeight: 500,
                                    letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.rust, mb: 0.75,
                                }}
                            >
                                Platform Configuration
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: fontDisplay, fontWeight: 600,
                                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                                    letterSpacing: '-0.035em', lineHeight: 1.05, color: palette.ink,
                                }}
                            >
                                System{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        fontStyle: 'italic',
                                        background: `linear-gradient(120deg, ${palette.rust}, ${palette.amber})`,
                                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    settings
                                </Box>
                            </Typography>
                            <Typography sx={{ color: palette.inkSoft, fontSize: '0.92rem', mt: 0.75, maxWidth: 560 }}>
                                Manage platform identity, security, services, subscriptions and administration from one centralized control centre.
                            </Typography>
                        </Box>
                    </Box>
                </motion.div>

                {/* Stats */}
                <Box
                    sx={{
                        display: 'grid', gap: 2, mb: 3,
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                    }}
                >
                    {statsData.map((s, i) => (
                        <StatCard key={s.label} {...s} delay={i * 0.06} loading={loading} />
                    ))}
                </Box>

                {/* Main Settings Panel with ALL tabs */}
                <GlassPanel accent={palette.rust} delay={0.1} padding={{ xs: 1.75, md: 2.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '70vh' }}>
                        {/* Tabs Sidebar */}
                        <Box sx={{
                            width: { xs: '100%', md: '240px' },
                            flexShrink: 0,
                            borderRight: { xs: 'none', md: `1px solid ${palette.hairlineSoft}` },
                            borderBottom: { xs: `1px solid ${palette.hairlineSoft}`, md: 'none' },
                            pb: { xs: 2, md: 0 },
                            mb: { xs: 2, md: 0 },
                        }}>
                            <Tabs
                                orientation={isMobile ? 'horizontal' : 'vertical'}
                                value={tabValue}
                                onChange={(_, newValue) => setTabValue(newValue)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    '& .MuiTab-root': {
                                        textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                                        fontFamily: fontBody, color: palette.inkLight,
                                        minHeight: 44, px: 1.5,
                                        justifyContent: { xs: 'center', md: 'flex-start' },
                                        '&.Mui-selected': { color: palette.ink },
                                        '&:hover': { color: palette.rust },
                                    },
                                    '& .MuiTabs-indicator': {
                                        height: { xs: 3, md: 'auto' },
                                        width: { xs: 'auto', md: 3 },
                                        borderRadius: { xs: '3px 3px 0 0', md: '0 3px 3px 0' },
                                        backgroundColor: tabs[tabValue]?.color || palette.rust,
                                    },
                                }}
                            >
                                {tabs.map((tab, index) => (
                                    <Tab
                                        key={index}
                                        icon={React.cloneElement(tab.icon, { sx: { fontSize: 17 } })}
                                        iconPosition="start"
                                        label={tab.label}
                                        sx={{
                                            '&.Mui-selected': { color: tab.color },
                                            '& .MuiTab-iconWrapper': { color: tabValue === index ? tab.color : palette.inkLight },
                                        }}
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        {/* Content Area */}
                        <Box sx={{ flex: 1, px: { xs: 0, md: 3 }, overflow: 'auto' }}>
                            <AnimatePresence mode="sync">
                                <TabPanel value={tabValue} index={0}>
                                    <GeneralSettingsComponent 
                                        settings={generalSettings} 
                                        setSettings={setGeneralSettings} 
                                        logoPreview={logoPreview} 
                                        handleLogoChange={handleLogoChange} 
                                        uploadingLogo={uploadingLogo} 
                                        saving={saving} 
                                        handleSave={handleSaveGeneral} 
                                    />
                                </TabPanel>

                                <TabPanel value={tabValue} index={1}>
                                    <SecuritySettingsComponent 
                                        settings={securitySettings} 
                                        setSettings={setSecuritySettings} 
                                        saving={saving} 
                                        handleSave={handleSaveSecurity} 
                                    />
                                </TabPanel>

                                <TabPanel value={tabValue} index={2}>
                                    <PlatformServicesSettings />
                                </TabPanel>

                                <TabPanel value={tabValue} index={3}>
                                    <SubscriptionSettingsComponent 
                                        settings={subscriptionSettings} 
                                        setSettings={setSubscriptionSettings} 
                                        saving={saving} 
                                        handleSave={handleSaveSubscription} 
                                        packages={packages} 
                                    />
                                </TabPanel>

                                <TabPanel value={tabValue} index={4}>
                                    <PackagesComponent 
                                        packages={packages} 
                                        openPackageDialog={openPackageDialog} 
                                        setOpenPackageDialog={setOpenPackageDialog} 
                                        packageForm={packageForm} 
                                        setPackageForm={setPackageForm} 
                                        editingPackage={editingPackage} 
                                        handleSavePackage={handleSavePackage} 
                                        handleDeletePackage={handleDeletePackage} 
                                        handleTogglePackageStatus={handleTogglePackageStatus} 
                                        handleOpenEditPackage={handleOpenEditPackage} 
                                        featureInput={featureInput} 
                                        setFeatureInput={setFeatureInput} 
                                        handleAddFeature={handleAddFeature} 
                                        handleRemoveFeature={handleRemoveFeature} 
                                        customSettingKey={customSettingKey} 
                                        setCustomSettingKey={setCustomSettingKey} 
                                        customSettingValue={customSettingValue} 
                                        setCustomSettingValue={setCustomSettingValue} 
                                        handleAddCustomSetting={handleAddCustomSetting} 
                                        handleRemoveCustomSetting={handleRemoveCustomSetting} 
                                        resetPackageForm={resetPackageForm} 
                                    />
                                </TabPanel>

                                <TabPanel value={tabValue} index={5}>
                                    <SuperAdminManagementComponent 
                                        superAdmins={superAdmins} 
                                        currentUserId={user?.uid} 
                                        openAddAdmin={openAddAdmin} 
                                        setOpenAddAdmin={setOpenAddAdmin} 
                                        openEditAdmin={openEditAdmin} 
                                        setOpenEditAdmin={setOpenEditAdmin} 
                                        newSuperAdmin={newSuperAdmin} 
                                        setNewSuperAdmin={setNewSuperAdmin} 
                                        editingAdmin={editingAdmin} 
                                        editAdminData={editAdminData} 
                                        setEditAdminData={setEditAdminData} 
                                        handleAddSuperAdmin={handleAddSuperAdmin} 
                                        handleEditSuperAdmin={handleEditSuperAdmin} 
                                        handleManualVerifyAdmin={handleManualVerifyAdmin} 
                                        handleResendAdminVerification={handleResendAdminVerification} 
                                        handleDeleteSuperAdmin={handleDeleteSuperAdmin} 
                                        handleOpenEditDialog={handleOpenEditDialog} 
                                        showDialogPassword={showDialogPassword} 
                                        setShowDialogPassword={setShowDialogPassword} 
                                    />
                                </TabPanel>

                                <TabPanel value={tabValue} index={6}>
                                    <DataManagementComponent 
                                        selectedCollections={selectedBackupCollections} 
                                        setSelectedCollections={setSelectedBackupCollections} 
                                        isBackingUp={isBackingUp} 
                                        handleDownloadBackup={handleDownloadBackup} 
                                        restoreFile={restoreFile} 
                                        setRestoreFile={setRestoreFile} 
                                        isRestoring={isRestoring} 
                                        handleRestoreBackup={handleRestoreBackup} 
                                        isWiping={isWiping} 
                                        handleWipeData={handleWipeData} 
                                    />
                                </TabPanel>
                            </AnimatePresence>
                        </Box>
                    </Box>
                </GlassPanel>
            </Container>

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.16)} transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb { background: ${rgba(palette.ink, 0.14)}; border-radius: 999px; }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.26)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default Settings;